import { normalizeSouthAfricanPhoneNumber } from '../../utils/validation/southAfricanPhone';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { capturedAffordability, individualBreakdownIssues, individualFinancialPayload, individualGrossIncomeIssue } from '../../services/individualAffordability';
import { zodResolver } from '@hookform/resolvers/zod';
import './LoanApplicationWizard.css';
import './IndividualJourney.css';
import { resetIndividualStepView } from './individualStepPresentation';
import { useCreateLoanApplication, useFindLeadByTask, useGetLedgerAccountsByCustomer } from '../../services/loanService';
import { existingExposure } from '../../constants/collateralValue';
import { WizardCustomerContext } from './wizardCustomer';
import StatementIqPanel from './steps/StatementIqPanel';
import IndividualQualificationStep from './steps/IndividualQualificationStep';
import {maritalStatusError} from './individualMaritalStatus';
import {individualValidationMessages} from './individualValidationMessages';
import IndividualErrorAlert from './IndividualErrorAlert';
import { STATEMENT_DRAFT_KEY } from '../../services/statementIq';
import {
  clearPersistedWizardState,
  DRAFT_QUERY_PARAM,
  isIndividualWizardState,
  readPersistedWizardState,
  useWizardDraftPersistence
} from './useWizardDraftPersistence';
import { useAddComment } from '../../services/hooks/loanCommentHooks';
import { useGetCustomer } from '../../services/customerService';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import axios from 'axios';
import type {
  LoanApplication,
  LoanApplicationType,
  CompanyDirector,
  RepresentativeCapacity,
  CompanyAddress,
  LoanLead
} from '../../services/schemas/loanServiceSchemas';
import type { Customer } from '../../services/schemas/customerServiceSchemas';
import Confetti from 'react-confetti';
import { AccountType } from '../../services/schemas/loanServiceSchemas/accountType';
import { LoanType } from '../../services/schemas/loanServiceSchemas/loanType';
import { MaritalStatus } from '../../services/schemas/loanServiceSchemas/maritalStatus';
import { RegistrationNumberType } from '../../services/schemas/loanServiceSchemas/registrationNumberType';
import { DocumentReferenceDocumentType } from '../../services/schemas/loanServiceSchemas/documentReferenceDocumentType';
import { ContactDetailCreateContactType } from '../../services/schemas/customerServiceSchemas/contactDetailCreateContactType';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { validateRegistrationNumber } from '../../utils/validation/legalEntityType';
import type { CompanyLegalEntityType } from '../../services/schemas/loanServiceSchemas/companyLegalEntityType';
import {
  isValidSouthAfricanTaxNumber,
  isValidSouthAfricanVatNumber,
  SOUTH_AFRICAN_TAX_NUMBER_ERROR,
  SOUTH_AFRICAN_VAT_NUMBER_ERROR
} from '../../utils/validation/taxVatNumber';
import {
  SOUTH_AFRICAN_ID_CHECKSUM_ERROR,
  SOUTH_AFRICAN_ID_DATE_ERROR,
  SOUTH_AFRICAN_ID_NUMBER_ERROR,
  SOUTH_AFRICAN_TAX_PIN_ERROR,
  isValidSouthAfricanIdDate,
  isValidSarsTaxPin,
  normalizeSarsTaxPin,
  passesSouthAfricanIdChecksum
} from '../../utils/validation/taxPinAndSouthAfricanId';
import {
  PASSPORT_NUMBER_ERROR,
  validatePassportNumber
} from '../../utils/validation/passportNumber';
import { validateBankAccountNumber } from '../../utils/validation/bankAccountNumber';

// Steps
import CustomerTypeStep from './steps/CustomerTypeStep';
import { validateIndividualNumber, validatePassportExpiry, usesCustomerIdentity } from '../../utils/validation/individualIdentification';
import NewCustomerSearchStep from './steps/NewCustomerSearchStep';
import LoanTypeStep from './steps/LoanTypeStep';
import IndividualInfoStep from './steps/IndividualInfoStep';
import CompanyInfoStep from './steps/CompanyInfoStep';
import BankAccountStep from './steps/BankAccountStep';
import FundingRequestStep from './steps/FundingRequestStep';
import ProductFundingDetailsStep from './steps/ProductFundingDetailsStep';
import { OfferedSecurityType } from '../../services/schemas/loanServiceSchemas';
import CollateralStep from './steps/CollateralStep';
import { individualProductErrors } from './individualProductRules';
import { individualQuotationRequired } from '../../services/individualPricingQuotations';
import { capturedIncomeProfile, individualIncomeErrors, individualEmploymentErrors, individualFinancialErrors, incomeProfileRequest } from './individualIncomeProfile';
import IndividualFundingStep from './steps/IndividualFundingStep';
import { individualJourneySteps, individualNextStep, individualPreviousStep } from './individualJourney';
import { returningCustomerJourney } from './returningCustomerJourney';
import ReviewStep from './steps/ReviewStep';
import { useQueryClient } from '@tanstack/react-query';
import { checkSavedIndividualIdentity, identityQueryKey, useIndividualIdentity } from '../../services/individualIdentityService';
import IndividualCreditCheckPanel, { IndividualCreditAssessment } from './steps/IndividualCreditCheckPanel';

// Modals
import CommentModal from '../modals/CommentModal';
import { maxLoanAmountFor } from '../../constants/loanLimits';
import { toLocalDateString } from '../../utils/localDate';

/** Statement IQ capture is keyed on an RSA ID number: not offered to passport or Customer ID applicants. */
const statementCaptureApplies = (idType?: string | null): boolean => idType !== 'PASSPORT' && !usesCustomerIdentity(idType);

// Add a type for the account type strings
type AccountTypeString = "SAVINGS" | "CURRENT_CHEQUE" | "TRANSMISSION" | "BUSINESS";
const SOUTH_AFRICAN_PHONE_REGEX = /^0\d{9}$/;

/** A director's own contact of one of these types from the customer record, the primary one first. */
const directorContact = (director: any, types: string[]): string => {
  const matches = (Array.isArray(director?.contacts) ? director.contacts : [])
    .filter((c: any) => c && types.includes(c.contactType) && typeof c.contactValue === 'string' && c.contactValue.trim());
  const primary = matches.find((c: any) => c.primary) || matches[0];
  return primary ? primary.contactValue.trim() : '';
};
const emptyToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;
const isNotFutureDate = (value?: string): boolean => {
  if (!value) return true;
  const normalized = value.split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return false;
  const today = toLocalDateString();
  return normalized <= today;
};

const loanCalculationModeEnum = z.enum(['AUTOMATIC', 'CUSTOM']);
type LoanCalculationModeType = z.infer<typeof loanCalculationModeEnum>;
const loanCalculationModeSchema = z
  .preprocess(
    (value) => {
      if (typeof value === 'string' && value.trim() === '') {
        return undefined;
      }
      return value;
    },
    loanCalculationModeEnum.optional()
  )
  .refine(
    (value): value is z.infer<typeof loanCalculationModeEnum> => value !== undefined,
    { message: 'Please select a loan structure' }
  );

// Define the form schema to match all steps and API payload
const loanApplicationSchema = z.object({
  loanType: z.enum(['PERSONAL', 'BUSINESS']),
  customerType: z.enum(['individual', 'company']),
  individual: z.object({
    fullNames: z.string().min(1, 'Full name is required'),
    firstName: z.string().trim().min(1, 'Given names are required'),
    lastName: z.string().trim().min(1, 'Surname is required'),
    // Blank only for a Customer ID client (historical, no document on file): validated per type in superRefine
    idNumber: z.string().trim(),
    idType: z.enum(['SA_ID', 'PASSPORT', 'CUSTOMER_ID']).optional(),
    passportExpiryDate: z.string().optional(),
    isPreviouslyDisadvantaged: z.boolean().optional(),
    maritalStatus: z.nativeEnum(MaritalStatus,{errorMap:(_issue,ctx)=>({message:maritalStatusError(ctx.data) || 'Select a marital status.'})}),
    occupation: z.string().optional(),
    employer: z.string().optional(),
    employmentType: z.enum(['PERMANENT', 'CONTRACT', 'SELF_EMPLOYED', 'UNEMPLOYED', '']),
    monthlySalary: z.number().min(0).optional(),
    dateOfBirth: z.string().optional(),
    incomeSource: z.string().optional(),
    monthlyNetIncome: z.number().optional(),
    monthlyLivingExpenses: z.number().optional(),
    address: z.object({
      streetAddress: z.string().min(1, 'Street address is required'),
      country: z.string().trim().min(1, 'Country is required'),
      city: z.string().min(1, 'City is required'),
      province: z.string().min(1, 'Province is required'),
      postalCode: z.string().min(1, 'Postal code is required')
    }),
    contactDetails: z.object({
      primaryPhone: z
        .string()
        .min(1, 'Primary phone is required')
        .regex(SOUTH_AFRICAN_PHONE_REGEX, 'Please enter a valid South African 10-digit phone number'),
      secondaryPhone: z
        .string()
        .regex(SOUTH_AFRICAN_PHONE_REGEX, 'Please enter a valid South African 10-digit phone number')
        .or(z.literal(''))
        .optional(),
      email: z.string().email('Invalid email address')
    }),
    dependents: z.number().min(0, 'Dependents must be a positive number')
  }).superRefine((person, ctx) => {
    if (!person.idNumber && !usesCustomerIdentity(person.idType))
      ctx.addIssue({code:z.ZodIssueCode.custom,path:['idNumber'],message:'Identification number is required'});
    const numberError = validateIndividualNumber(person.idNumber, person.idType);
    if (numberError !== true) ctx.addIssue({code:z.ZodIssueCode.custom,path:['idNumber'],message:numberError});
    if (person.idType === 'PASSPORT') {
      const expiryError = validatePassportExpiry(person.passportExpiryDate);
      if (expiryError !== true) ctx.addIssue({code:z.ZodIssueCode.custom,path:['passportExpiryDate'],message:expiryError});
    }
  }).optional(),
  company: z.object({
    companyName: z.string().min(1, 'Company name is required'),
    // Validated against legalEntityType in the superRefine below, not here: a trust's IT
    // number, an NPO number and a practice number are all valid, and which one applies is not
    // known until the entity type is read. This schema previously hard-required a CIPC number,
    // which is why those clients could not be onboarded at all.
    // Required per legal form in the superRefine (validateRegistrationNumber) - blank only for Customer ID,
    // where the customer service fills in the customer number
    companyRegNumber: z
      .string()
      .trim(),
    legalEntityType: z.string().min(1, 'Select the legal entity type'),
    // Optional: what kind of number the line above holds, where the legal form alone does not say
    // (an Inc law firm known only by its practice number). The legal form drives validation.
    registrationNumberType: z.string().optional(),
    // The contact person IS the company representative - the codebase has always treated them
    // as one, it simply had nowhere to store them and fell back to the first director. Their
    // name comes from contactPersonFirstName / contactPersonLastName; these are the things
    // about that person which had no home before.
    representativeIdNumber: z.string().optional(),
    representativeCapacity: z.string().optional(),
    representativeMandate: z.string().optional(),
    // Which director is the contact person, when one is (finding #4); kept on the form only
    contactDirectorIndex: z.string().optional(),
    taxNumber: z
      .string()
      .trim()
      .optional()
      .refine(
        (value) => !value || isValidSouthAfricanTaxNumber(value),
        SOUTH_AFRICAN_TAX_NUMBER_ERROR
      ),
    vatNumber: z
      .string()
      .optional()
      .transform((value) => value?.trim())
      .refine(
        (value) => !value || isValidSouthAfricanVatNumber(value),
        SOUTH_AFRICAN_VAT_NUMBER_ERROR
      ),
    contactPersonFirstName: z.string().min(1, 'Contact person first name is required'),
    contactPersonLastName: z.string().min(1, 'Contact person last name is required'),
    contactTel: z
      .string()
      .min(1, 'Contact telephone is required')
      .regex(SOUTH_AFRICAN_PHONE_REGEX, 'Please enter a valid South African 10-digit phone number'),
    contactEmail: z.string().email('Invalid email address'),
    address: z.object({
      type: z.enum(['BUSINESS', 'RESIDENTIAL']),
      lineOne: z.string().min(1, 'Address line 1 is required'),
      lineTwo: z.string().optional(),
      province: z.string().optional(),
      suburb: z.string().optional(),
      city: z.string().min(1, 'City is required'),
      code: z.string().min(1, 'Postal code is required')
    }),
    // No longer captured on the application (business test, 22 Oct 2026); kept when a customer has one
    representativeOccupation: z.string().optional(),
    turnover: z.number().min(0, 'Turnover must be a positive number'),
    reasonForFunding: z.string().optional(),
    industrySector: z.string().optional(),
    incomeType: z.preprocess(
      emptyToUndefined,
      z
        .enum(['PO', 'TENDER', 'PRODUCTS_AND_SERVICES', 'OTHER'])
        .optional()
        .refine((value) => value !== undefined, 'Source of income is required')
    ),
    incomeDescription: z.string().optional(),
    industryCategory: z.preprocess(
      emptyToUndefined,
      z.enum(['GOVERNMENT', 'PRIVATE_SECTOR', 'HOUSEHOLD', 'PERSONAL', 'OTHER']).optional()
    ),
    industryDescription: z.string().optional(),
    taxPin: z
      .string()
      .optional()
      .transform((value) => normalizeSarsTaxPin(value))
      .refine(
        (value) => !value || isValidSarsTaxPin(value),
        SOUTH_AFRICAN_TAX_PIN_ERROR
      ),
    directors: z.array(z.object({
      firstName: z.string().min(1, 'First name is required'),
      lastName: z.string().min(1, 'Last name is required'),
      idType: z.preprocess(
        emptyToUndefined,
        z
          .enum(['SA_ID', 'PASSPORT'])
          .optional()
          .refine((value) => value !== undefined, 'ID Type is required')
      ),
      idNumber: z.string().trim().min(1, 'ID number is required'),
      nationality: z.string().min(1, 'Nationality is required'),
      dateOfBirth: z.string().min(1, 'Date of birth is required'),
      designation: z.string().min(1, 'Designation is required'),
      // The director's own contacts, for messages to them personally such as birthday greetings
      email: z.string().trim().email('Please enter a valid email address').optional().or(z.literal('')),
      cellNumber: z
        .string()
        .trim()
        .regex(SOUTH_AFRICAN_PHONE_REGEX, 'Please enter a valid South African 10-digit cell number')
        .optional()
        .or(z.literal('')),
      ownershipPercentage: z.number().min(0).max(100).optional(),
      isExecutive: z.boolean().optional(),
      appointmentDate: z.string().optional(),
      gender: z.preprocess(
        emptyToUndefined,
        z
          .enum(['MALE', 'FEMALE', 'OTHER', 'PREFER_NOT_TO_SAY'])
          .optional()
          .refine((value) => value !== undefined, 'Gender is required')
      ),
      isDisabled: z.boolean().optional(),
      isPreviouslyDisadvantaged: z.boolean().optional(),
      race: z.preprocess(
        emptyToUndefined,
        z
          .enum(['BLACK', 'COLOURED', 'WHITE', 'ASIAN', 'OTHER', 'PREFER_NOT_TO_SAY'])
          .optional()
          .refine((value) => value !== undefined, 'Race is required')
      ),
      addresses: z.array(z.object({
        addressType: z.enum(['RESIDENTIAL', 'POSTAL', 'BUSINESS']),
        streetAddress: z.string().min(1, 'Street address is required'),
        suburb: z.string().optional(),
        city: z.string().min(1, 'City is required'),
        stateProvince: z.string().optional(),
        postalCode: z.string().optional(),
        country: z.string().min(1, 'Country is required'),
        isPrimary: z.boolean().optional()
      })).optional()
    }).superRefine((director, ctx) => {
      if (director.appointmentDate && !isNotFutureDate(director.appointmentDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Appointment date cannot be in the future',
          path: ['appointmentDate']
        });
      }

      if (director.idType === 'SA_ID') {
        if (!/^[0-9]{13}$/.test(director.idNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SOUTH_AFRICAN_ID_NUMBER_ERROR,
            path: ['idNumber']
          });
          return;
        }

        if (!isValidSouthAfricanIdDate(director.idNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SOUTH_AFRICAN_ID_DATE_ERROR,
            path: ['idNumber']
          });
          return;
        }

        const citizenshipDigit = director.idNumber[10];
        if (citizenshipDigit !== '0' && citizenshipDigit !== '1') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SOUTH_AFRICAN_ID_NUMBER_ERROR,
            path: ['idNumber']
          });
          return;
        }

        if (!passesSouthAfricanIdChecksum(director.idNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SOUTH_AFRICAN_ID_CHECKSUM_ERROR,
            path: ['idNumber']
          });
        }
      } else if (director.idType === 'PASSPORT') {
        const validation = validatePassportNumber(director.idNumber);
        if (validation !== true) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: validation || PASSPORT_NUMBER_ERROR,
            path: ['idNumber']
          });
        }
      }
    })).min(1, 'At least one director is required')
      // Two directors cannot share an ID number. Left unchecked the duplicate is not merely
      // wrong, it is lost: the corporate holds its directors in a Set keyed on the ID number,
      // so the second one is silently dropped before it reaches the database and the save
      // still reports success. Flag it on the offending row instead.
      .superRefine((directors, ctx) => {
        const seen = new Map<string, number>();

        directors.forEach((director, index) => {
          const idNumber = director?.idNumber?.trim();
          if (!idNumber) return;

          const firstIndex = seen.get(idNumber);
          if (firstIndex === undefined) {
            seen.set(idNumber, index);
            return;
          }

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `This ID number is already used by director ${firstIndex + 1}. Each director must have their own ID number.`,
            path: [index, 'idNumber'],
          });
        });
      })
  }).superRefine((company, ctx) => {
    // The registration number is checked against the chosen legal form. A trust carries an IT
    // number, an NPO a "123-456 NPO" number, a healthcare practice a 7-digit practice number,
    // and a sole proprietor the owner's ID. Insisting on a CIPC number for all of them is what
    // stopped those clients being onboarded.
    const result = validateRegistrationNumber(company.companyRegNumber, company.legalEntityType);
    if (result !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: result,
        path: ['companyRegNumber'],
      });
    }
  }).optional(),
  // Shared financial record: individual capture supplies its detailed monthly declaration.
  // Company income, expense and household conventions stay unchanged.
  financial: z.object({
    individualBreakdown: z.record(z.number().min(0).optional()).optional(),
    livingExpenses: z.number().min(0).optional(),
    otherLivingExpenses: z.number().min(0).optional(),
    otherLivingExpenseItems: z.array(z.object({ name: z.string().trim().min(1).max(255),
      amount: z.number().min(0).refine(value => Math.abs(value * 100 - Math.round(value * 100)) < 0.000001, 'Enter a monthly amount to cents.') })).optional(),
    maintenance: z.number().min(0).optional(),
    affordabilityCaptured: z.boolean().optional(),
    income: z.record(z.number().min(0)).optional(),
    expenses: z.record(z.number().min(0)).optional(),
    household: z.record(z.number().min(0)).optional(),
    hasExistingCreditObligations: z.boolean().optional()
  }),
  bankAccount: z.object({
    accountHolderName: z.string().min(1, 'Account holder name is required'),
    bankName: z.string().min(1, 'Bank name is required'),
    accountNumber: z.string().trim().min(1, 'Account number is required'),
    accountType: z.enum(['SAVINGS', 'CURRENT_CHEQUE', 'TRANSMISSION', 'BUSINESS']),
    branchCode: z.string().min(1, 'Branch code is required')
  }).superRefine((bankAccount, ctx) => {
    const result = validateBankAccountNumber(bankAccount.accountNumber, bankAccount.bankName);
    if (result !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: result,
        path: ['accountNumber']
      });
    }
  }),
  loan: z.object({
    individualPricingQuote: z.any().optional(),
    individualPricingInputs: z.any().optional(),
    individualQuotationState: z.enum(['incomplete','calculating','ready','error']).optional(),
    individualAffordabilityPreview: z.any().optional(),
    individualQualificationSettings: z.any().optional(),
    individualQualificationInputs: z.any().optional(),
    calculationMode: loanCalculationModeSchema,
    loanProductType: z.enum(['STANDARD', 'TIRHELO', 'SEDFA']).optional(),
    customLoanType: z.enum(['ONCE_OFF', 'INSTALLMENTS']).optional(),
    loanAmount: z.number().min(1, 'Loan amount must be greater than 0'),
    loanTerm: z.number().min(1, 'Loan term must be greater than 0'),
    loanPurpose: z.string().min(1, 'Loan purpose is required'),
    securityType: z.enum(['SECURED', 'UNSECURED']),
    interestRate: z.number().min(0).max(100).optional(),
    adminFee: z.number().min(0).optional(),
    // Default terms: what is charged if the client defaults. Optional - can be set later.
    defaultInterestRate: z.number().min(0, 'Default interest rate cannot be negative').max(100).optional(),
    defaultChargeFrequency: z.enum(['ONCE_OFF', 'WEEKLY', 'MONTHLY', 'CUSTOM_DAYS']).optional(),
    defaultChargeIntervalDays: z.number().int('Charge interval must be whole days').min(1, 'Charge interval must be at least 1 day').optional(),
    defaultGraceDays: z.number().int('Grace days must be whole days').min(0, 'Grace days cannot be negative').optional(),
    totalRepayment: z.number().min(0).optional(),
    interestAmount: z.number().min(0).optional(),
    dueDate: z.string().optional(),
    commencementDate: z.string().min(1, 'Commencement date is required'),
    numberOfInstallments: z.number().min(1).max(12).optional(),
    // Type of Funding Requested + Project / Purchase-Order information
    fundingType: z.string().optional(),
    projectClientName: z.string().optional(),
    projectDescription: z.string().optional(),
    projectReferenceNumber: z.string().optional(),
    projectValue: z.number().min(0).optional(),
    projectExpectedCompletionDate: z.string().optional(),
    projectExpectedPaymentDate: z.string().optional(),
    // Funding request captured at origination
    requiredFundingDate: z.string().optional(),
    proposedRepaymentDate: z.string().optional(),
    customerContribution: z.number().min(0).optional(),
    primaryRepaymentSource: z.string().optional(),
    securityAvailability: z.enum(['YES', 'NO', 'TO_BE_CONFIRMED']).optional(),
    securityTypesOffered: z.array(z.string()).optional(),
    // Invoice discounting
    invoiceAmount: z.number().min(0).optional(),
    invoiceDate: z.string().optional(),
    invoiceDebtorName: z.string().optional(),
    invoicePaymentTerms: z.string().optional(),
    invoiceExpectedPaymentDate: z.string().optional(),
    invoiceAmountRequested: z.number().min(0).optional(),
    // Bridge funding
    bridgeUnderlyingTransaction: z.string().optional(),
    bridgeExpectedIncomingAmount: z.number().min(0).optional(),
    bridgeSourceOfExpectedPayment: z.string().optional(),
    bridgePayerName: z.string().optional(),
    bridgeReasonForFundingGap: z.string().optional(),
    bridgeExpectedPaymentDate: z.string().optional(),
    // Purchase order funding
    poCostToFulfil: z.number().min(0).optional(),
    supplierName: z.string().optional(),
    // Invoice discounting (additional)
    invoiceGoodsDescription: z.string().optional(),
    invoiceStatus: z.string().optional(),
    // Working capital
    workingCapitalMonthlyTurnover: z.number().min(0).optional(),
    workingCapitalOperatingCycleDays: z.number().min(0).optional(),
    workingCapitalCurrentDebtors: z.number().min(0).optional(),
    workingCapitalCurrentCreditors: z.number().min(0).optional(),
    // Project finance (additional)
    projectTotalCost: z.number().min(0).optional(),
    projectStartDate: z.string().optional(),
    projectPaymentMilestones: z.string().optional(),
    projectExpectedFirstPaymentDate: z.string().optional(),
    // Equipment / vehicle (asset) funding
    assetDescription: z.string().optional(),
    assetPurchasePrice: z.number().min(0).optional(),
    assetPurpose: z.string().optional(),
    assetExpectedMonthlyIncome: z.number().min(0).optional(),
    assetUsefulLifeMonths: z.number().min(0).optional(),
    assetInsuranceStatus: z.string().optional()
  }).superRefine((loan, ctx) => {
    if (loan.defaultChargeFrequency === 'CUSTOM_DAYS' && !loan.defaultChargeIntervalDays) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['defaultChargeIntervalDays'],
        message: 'Enter how many days apart the default interest is charged',
      });
    }

    const loanProductType =
      loan.loanProductType || (loan.calculationMode === 'AUTOMATIC' ? 'STANDARD' : 'TIRHELO');
    const isTirhelo = loanProductType === 'TIRHELO';
    const maxLoanAmount = maxLoanAmountFor(loanProductType === 'SEDFA');
    const requiresDueDate = loan.customLoanType === 'ONCE_OFF';

    if (!isTirhelo) {
      if (loan.loanAmount < 3000) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Loan amount must be at least R3,000',
          path: ['loanAmount']
        });
      }

      if (loan.loanAmount > maxLoanAmount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            loanProductType === 'SEDFA'
              ? 'SEDFA Loan amount cannot exceed R250,000'
              : 'Loan amount cannot exceed R1,500,000',
          path: ['loanAmount']
        });
      }
    }

    if (requiresDueDate && !loan.dueDate?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Due date is required',
        path: ['dueDate']
      });
    }

    if (loan.customLoanType === 'INSTALLMENTS' && !loan.numberOfInstallments) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Number of installments is required',
        path: ['numberOfInstallments']
      });
    }
  }),
  collaterals: z.array(z.object({
    assetDescription: z.string().min(1, 'Asset description is required'),
    make: z.string().optional(),
    yearModel: z.string().optional(),
    registrationNumber: z.string().optional(),
    otherDetails: z.string().optional(),
    ownerName: z.string().min(1, 'Owner name is required'),
    // Accepts an SA ID number, a company/trust registration number or a
    // passport number. Capped at 50 to match the owner_id column.
    ownerId: z.string()
      .min(1, 'Owner ID or registration number is required')
      .max(50, 'Owner ID or registration number cannot be longer than 50 characters'),
    value: z.number().min(0, 'Value must be a positive number'),
    // A vehicle's mileage and retail value (findings #10, #11)
    mileageKm: z.number().int('Mileage is in whole km').min(0, 'Mileage cannot be negative').optional(),
    retailValue: z.number().min(0, 'Retail value cannot be negative').optional(),
    retailValueSource: z.string().max(60).optional(),
    // A vehicle's insurance (finding #10)
    insured: z.boolean().optional(),
    insurer: z.string().max(100).optional(),
    insurancePolicyNumber: z.string().max(60).optional()
  })).optional()
}).superRefine((data, ctx) => {
  // A director who is the contact person supplies the business's contact number and email
  const contactIndex = data.company?.contactDirectorIndex;
  if (data.loanType !== 'PERSONAL' && contactIndex !== undefined && contactIndex !== '') {
    const director = data.company?.directors?.[Number(contactIndex)];
    if (director && !director.cellNumber) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['company', 'directors', Number(contactIndex), 'cellNumber'],
      message: 'This director is the contact person: their cell number is needed' });
    if (director && !director.email) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['company', 'directors', Number(contactIndex), 'email'],
      message: 'This director is the contact person: their email is needed' });
  }
  if (data.loanType === 'PERSONAL') {
    individualBreakdownIssues(data.financial).forEach(issue => ctx.addIssue({ code: z.ZodIssueCode.custom,
      path: ['financial', 'individualBreakdown', issue.name], message: issue.message }));
    individualIncomeErrors(data.individual).forEach(({field, message}) =>
      ctx.addIssue({code: z.ZodIssueCode.custom, path: ['individual', field], message}));
    const quotationRequired = individualQuotationRequired(data.loan);
    if (quotationRequired) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['loan', 'individualPricingQuote'], message: quotationRequired });
    individualProductErrors(data.loan).forEach(({field, message}) =>
      ctx.addIssue({code: z.ZodIssueCode.custom, path: ['loan', field], message}));
  }
});

// Define the form values type based on the schema
type FormValues = z.infer<typeof loanApplicationSchema>;

// Define financial category items for initialization
const financialCategories = {
  income: [
    'grossIncome', 'netIncome', 'otherIncome', 'monthlyRevenue'
  ],
  expenses: [
    'bondInstalment', 'rent', 'vehicleFinance', 'personalLoan', 'creditCard',
    'rentLease', 'utilities', 'salaries', 'loans'
  ],
  household: [
    'telkomCell', 'education', 'storeCards', 'ratesAndTaxes', 'groceries',
    'fuelTravel', 'insurance'
  ]
};

// Create default financial objects with 0.00 values
const createDefaultFinancialObject = (keys: string[]) => {
  const obj: Record<string, number> = {};
  keys.forEach(key => {
    obj[key] = 0;
  });
  return obj;
};

// Map of bank name abbreviations to full names for dropdown matching
const bankNameMapping: Record<string, string> = {
  'FNB': 'First National Bank (FNB)',
  'ABSA': 'ABSA Bank',
  'CAPITEC': 'Capitec Bank',
  'NEDBANK': 'Nedbank',
  'STANDARD BANK': 'Standard Bank',
  'AFRICAN BANK': 'African Bank',
  'BIDVEST': 'Bidvest Bank',
  'DISCOVERY': 'Discovery Bank',
  'INVESTEC': 'Investec',
  'TYME': 'TymeBank',
  'BANK ZERO': 'Bank Zero'
};

// Function to normalize bank name to match dropdown options
const normalizeBankName = (bankName: string): string => {
  if (!bankName) return '';

  // First check if it's already a full name that matches exactly
  const southAfricanBanks = [
    'ABSA Bank', 'Capitec Bank', 'First National Bank (FNB)', 'Nedbank',
    'Standard Bank', 'African Bank', 'Bidvest Bank', 'Discovery Bank',
    'Investec', 'TymeBank', 'Bank Zero'
  ];

  if (southAfricanBanks.includes(bankName)) {
    return bankName;
  }

  // Check case-insensitive mappings
  const uppercaseBankName = bankName.toUpperCase();
  for (const [abbr, fullName] of Object.entries(bankNameMapping)) {
    if (uppercaseBankName.includes(abbr)) {
      return fullName;
    }
  }

  // If no match found, return Other or the original
  return 'Other';
};


export default function LoanApplicationWizard() {
  const { leadId, applicationId: paramApplicationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const authUser = useSelector((state: RootState) => state.auth.user);
  const [qualificationReady, setQualificationReady] = useState(false);
  const [activeStep, setActiveStep] = useState(() => {
    const saved = readPersistedWizardState();
    return isIndividualWizardState(saved) ? saved.activeStep : 0;
  });
  const [isLoading, setIsLoading] = useState(false);
  // Synchronous lock against a second submit. `isLoading` only disables the button after the
  // next render, and pressing Enter in a field submits the form without touching the button,
  // so either could post the same application twice. The server cannot reject the second
  // one: a second application for the same client on the same day is legitimate (-1 suffix).
  const submitInFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [stepValidationMessages, setStepValidationMessages] = useState<string[]>([]);
  const [validationAttempt, setValidationAttempt] = useState(0);
  useEffect(() => { setStepValidationMessages([]); }, [activeStep]);
  const [clientFullScreen, setClientFullScreen] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState<string | null>(null);
  const [submittedApplicationId, setSubmittedApplicationId] = useState<string | null>(null);
  const [windowDimension, setWindowDimension] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [leadData, setLeadData] = useState<LoanLead | null>(null);
  const [isLoadingLead, setIsLoadingLead] = useState(false);
  // True when we arrived here by converting a lead. In that case the Customer Type
  // and Customer Search steps are irrelevant, so we skip them and start at Customer Info.
  const [isLeadConversion, setIsLeadConversion] = useState(false);
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const customerIdentity = useIndividualIdentity(customerData?.type === 'INDIVIDUAL' ? customerData.id : undefined);
  const identityQueries = useQueryClient();
  const handleIndividualCreditAssessment = (assessment: IndividualCreditAssessment | null) => {
    setIndividualCreditAssessment(assessment);
    if (assessment?.result.report?.id && customerData?.type === 'INDIVIDUAL' && customerData.id) {
      const customerId = customerData.id;
      void checkSavedIndividualIdentity(customerId, assessment.result.report.id)
        .then(status => identityQueries.setQueryData(identityQueryKey(customerId), status))
        .catch(() => toast.warning('Credit report saved. Check identity from the customer profile.'));
    }
  };
  const [searchParams, setSearchParams] = useState<{
    registrationNumber?: string;
    idNumber?: string;
    idType?: 'SA_ID' | 'PASSPORT' | 'CUSTOMER_ID';
  }>({});
  const [isExistingCustomer, setIsExistingCustomer] = useState<boolean | null>(null);
  const [individualCreditAssessment, setIndividualCreditAssessment] = useState<IndividualCreditAssessment | null>(null);
  const [statementDraftId,setStatementDraftId]=useState<string|undefined>(()=>{
    const saved=readPersistedWizardState();
    return isIndividualWizardState(saved) ? saved.statementDraftId : sessionStorage.getItem(STATEMENT_DRAFT_KEY) || undefined;
  });
  const [isSearching, setIsSearching] = useState(false);
  // Add state to track if loan is unsecured
  const [isUnsecuredLoan, setIsUnsecuredLoan] = useState(false);
  // Add state to track if submission is allowed
  const [allowSubmission, setAllowSubmission] = useState(false);
  // Add flag to track if we just arrived at review step
  const [justArrivedAtReviewStep, setJustArrivedAtReviewStep] = useState(false);
  // Comment modal state
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  // Abandon application modal state
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  // Collected comments for new applications
  const [collectedComments, setCollectedComments] = useState<string[]>([]);

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const taskId = queryParams.get('taskId');
  const navState = (location.state as {
    registrationNumber?: string;
    idNumber?: string;
    idType?: 'SA_ID' | 'PASSPORT' | 'CUSTOMER_ID';
    customerType?: 'individual' | 'company';
    customerNumber?: string;
    customerId?: string;
    customerName?: string;
  }) || {};
  const navPrefillDone = useRef(false);
  // What a returning customer already owes on open loan accounts, for the collateral check (finding #10)
  const exposureCustomerNumber = customerData?.customerNumber || navState.customerNumber || '';
  const { data: customerAccounts } = useGetLedgerAccountsByCustomer({ customerNumber: exposureCustomerNumber },
    { query: { enabled: !!exposureCustomerNumber, retry: false } });
  const customerExposure = useMemo(() => existingExposure(customerAccounts), [customerAccounts]);
  // Resuming a saved draft: the customer prefill the page was first opened with has already been captured
  const isResumingSavedDraft = queryParams.has(DRAFT_QUERY_PARAM);

  const isRestrictedCustomer = (status?: string) => {
    if (!status) return false;
    const normalized = status.toUpperCase();
    return normalized === 'INACTIVE' || normalized === 'SUSPENDED' || normalized === 'BLACKLISTED';
  };

  const showCustomerRestrictionToast = (status?: string) => {
    const normalized = status?.toUpperCase();
    const isBlacklisted = normalized === 'SUSPENDED' || normalized === 'BLACKLISTED';
    const isInactive = normalized === 'INACTIVE';
    const toastId = `customer-restriction-${normalized || 'unknown'}`;

    toast(isInactive ? 'Customer is inactive' : 'Customer is blacklisted', {
      toastId,
      type: isBlacklisted ? 'warning' : 'error',
      icon: isInactive
        ? <span role="img" aria-label="Inactive">⏸️</span>
        : isBlacklisted
        ? <span role="img" aria-label="Blacklisted">⛔</span>
        : <span role="img" aria-label="Suspended">⏸️</span>,
      autoClose: 4500,
      closeOnClick: true
    });
  };

  // Query for finding customer - Only execute when explicitly triggered by search button
  const customerQuery = useGetCustomer(
      (searchParams.registrationNumber || searchParams.idNumber)
          ? {
            registrationNumber: searchParams.registrationNumber,
            idNumber: searchParams.idNumber,
            ...(searchParams.idType === 'PASSPORT' && {idType:searchParams.idType}),
          }
          : undefined,
      {
        query: {
          // Only enable the query when we explicitly want to search
          enabled: isSearching && !!(searchParams.registrationNumber || searchParams.idNumber)
        }
      }
  ) as { isSuccess: boolean; isError: boolean; data?: Customer; isLoading: boolean };

  // Effect to initialize state on component mount
  useEffect(() => {
    // Reset search state when component mounts to prevent automatic search
    setIsSearching(false);
    setSearchParams({});
    setIsExistingCustomer(null);
    setCustomerData(null);
  }, []);

  // If navigation provided customer identifiers, auto-run the search
  useEffect(() => {
    if (isResumingSavedDraft) return;
    if (navState.registrationNumber || navState.idNumber) {
      setSearchParams({
        registrationNumber: navState.registrationNumber,
        idNumber: navState.idNumber,
        idType: navState.idType,
      });
      setIsSearching(true);
      setActiveStep(2);
    }
  }, [navState.registrationNumber, navState.idNumber, navState.idType]);

  // When we arrived from customer list and the lookup succeeds, jump straight to step 3 (index 2)
  useEffect(() => {
    if (navPrefillDone.current || isResumingSavedDraft) return;
    if ((navState.registrationNumber || navState.idNumber) && customerQuery.isSuccess && customerQuery.data) {
      if (isRestrictedCustomer(customerQuery.data.status)) {
        showCustomerRestrictionToast(customerQuery.data.status);
        setIsExistingCustomer(false);
        setActiveStep(1);
        navPrefillDone.current = true;
        return;
      }
      populateFormWithCustomerData(customerQuery.data);
      setIsExistingCustomer(true);
      setActiveStep(2);
      navPrefillDone.current = true;
    }
  }, [navState.registrationNumber, navState.idNumber, customerQuery.isSuccess, customerQuery.data]);

  // Effect to handle customer data when it's loaded
  useEffect(() => {
    if (customerQuery.isSuccess && customerQuery.data) {
      if (isRestrictedCustomer(customerQuery.data.status)) {
        setIsExistingCustomer(false);
        setIsSearching(false);
        return;
      }
      setCustomerData(customerQuery.data);
      setIsExistingCustomer(true);
      setIsSearching(false);
    } else if (customerQuery.isError) {
      console.error('Error finding customer');
      setIsExistingCustomer(false);
      setIsSearching(false);
      setError('Failed to find customer. Please try again or create a new application.');
    } else if (customerQuery.isSuccess && !customerQuery.data) {
      setIsExistingCustomer(false);
      setIsSearching(false);
    }
  }, [customerQuery.isSuccess, customerQuery.isError, customerQuery.data]);

  // Mutation for finding lead by task
  const findLeadByTaskMutation = useFindLeadByTask({
    mutation: {
      onSuccess: (data) => {
        setIsLoadingLead(false);
        if (data) {
          setLeadData(data);
          // Pre-populate form with lead data
          populateFormWithLeadData(data);
          // Lead conversion: skip Customer Type + Customer Search, start at Customer Info (step 3)
          setIsLeadConversion(true);
          setActiveStep(2);
        } else {
          setError("No lead data found for this task");
        }
      },
      onError: (error) => {
        setIsLoadingLead(false);
        setError(`Failed to retrieve lead data: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  });

  // Function to populate form with customer data
  const populateFormWithCustomerData = (customer: Customer) => {
    console.groupCollapsed('[LoanApp][Prefill] populateFormWithCustomerData input');
    console.log('customerId:', customer.id);
    console.log('customerType:', customer.type);
    console.log('info.type:', (customer.info as any)?.type);
    console.log('representativeOccupation (raw):', (customer.info as any)?.representativeOccupation);
    console.log('firstDirectorDesignation (raw):', (customer.info as any)?.directors?.[0]?.designation);
    console.log('raw customer payload:', customer);
    console.groupEnd();

    // Determine customer type based on customer data
    const journey = returningCustomerJourney(customer.type);
    const { customerType } = journey;

    // Create a base form data object with the common fields
    const formData: any = {
      ...journey,
    };

    // Get contact details
    const emailContact = customer.contacts?.find(contact =>
        contact.contactType === ContactDetailCreateContactType.EMAIL
    );

    const phoneContact = customer.contacts?.find(contact =>
        (contact.contactType === ContactDetailCreateContactType.MOBILE ||
            contact.contactType === ContactDetailCreateContactType.WHATSAPP ||
            contact.contactType === ContactDetailCreateContactType.SMS) &&
        contact.primary === true
    ) || customer.contacts?.find(contact =>
        contact.contactType === ContactDetailCreateContactType.MOBILE ||
        contact.contactType === ContactDetailCreateContactType.WHATSAPP ||
        contact.contactType === ContactDetailCreateContactType.SMS
    );

    // Get address if available
    const address = customer.addresses && customer.addresses.length > 0
        ? customer.addresses[0]
        : undefined;

    // Add individual data if customer type is individual
    if (customerType === 'individual' && customer.info && customer.info.type === 'INDIVIDUAL') {
      const individual = customer.info as any;
      formData.individual = {
        fullNames: `${individual.firstName || ''} ${individual.lastName || ''}`.trim(),
        firstName: individual.firstName || '',
        lastName: individual.lastName || '',
        idNumber: individual.idNumber || '',
        idType: individual.idType || 'SA_ID',
        passportExpiryDate: individual.passportExpiryDate || '',
        contactDetails: {
          primaryPhone: normalizeSouthAfricanPhoneNumber(phoneContact?.contactValue) || '',
          email: emailContact?.contactValue || '',
          secondaryPhone: ''
        },
        address: {
          streetAddress: address?.streetAddress || '',
          country: address?.country || '',
          city: address?.city || '',
          province: address?.stateProvince || '',
          postalCode: address?.postalCode || ''
        },
        maritalStatus: individual.maritalStatus || '',
        occupation: individual.occupation || '',
        ...capturedIncomeProfile(individual),
        monthlyNetIncome: individual.monthlyNetIncome ?? 0,
        monthlySalary: individual.monthlySalary ?? undefined,
        dependents: individual.dependants || 0
      };
    }
    // Add company data if customer type is company
    else if (customerType === 'company' && customer.info && customer.info.type === 'CORPORATE') {
      const corporate = customer.info as any;

      // Helper function to normalize date format (YYYY-MM-DD) for HTML date inputs
      const normalizeDate = (dateStr: string | undefined): string => {
        if (!dateStr) return '';
        
        // If date includes time component, extract just the date part
        let dateOnly = dateStr.split('T')[0].split(' ')[0]; // Handle both 'T' and space separators
        
        // Validate it's in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
          return dateOnly;
        }
        
        // Try to parse and reformat if it's a valid date
        try {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
        } catch (e) {
          // If parsing fails, return original string
        }
        
        return dateStr;
      };

      // Extract directors data
      const directors = corporate.directors?.map((director: any) => {
        const idNumber = director.idNumber || '';
        // Infer idType from ID number length if not provided
        // 13 digits = South African ID, otherwise assume PASSPORT or leave undefined
        let idType = director.idType;
        if (!idType && idNumber) {
          idType = idNumber.length === 13 ? 'SA_ID' : 'PASSPORT';
        }
        
        return {
          firstName: director.firstName || '',
          lastName: director.lastName || '',
          idType: idType as 'SA_ID' | 'PASSPORT' | undefined,
          idNumber: idNumber,
          nationality: director.nationality || '',
          dateOfBirth: normalizeDate(director.dateOfBirth),
          designation: director.designation || '',
          ownershipPercentage: director.ownershipPercentage || 0,
          isExecutive: director.isExecutive || false,
          appointmentDate: normalizeDate(director.appointmentDate),
          gender: director.gender || undefined,
          isDisabled: director.isDisabled || false,
          isPreviouslyDisadvantaged: director.isPreviouslyDisadvantaged || false,
          race: director.race || undefined,
          email: directorContact(director, ['EMAIL']),
          cellNumber: normalizeSouthAfricanPhoneNumber(directorContact(director, ['MOBILE', 'SMS', 'WHATSAPP'])),
          addresses: director.addresses || []
        };
      }) || [{ 
        firstName: '', 
        lastName: '', 
        idType: undefined,
        idNumber: '', 
        nationality: '', 
        dateOfBirth: '', 
        designation: '', 
        ownershipPercentage: 0, 
        isExecutive: false, 
        appointmentDate: '', 
        gender: undefined, 
        isDisabled: false, 
        isPreviouslyDisadvantaged: false, 
        race: undefined, 
        addresses: [] 
      }];

      // Use the first director's information for contact person details if available
      const firstDirector = directors.length > 0 ? directors[0] : null;
      const contactPersonFirstName = corporate.representativeFirstName || (firstDirector ? firstDirector.firstName : '');
      const contactPersonLastName = corporate.representativeLastName || (firstDirector ? firstDirector.lastName : '');
      
      // Auto-populate representativeOccupation from first director's designation if not provided
      const representativeOccupation = corporate.representativeOccupation 
        || (firstDirector ? (firstDirector as any).designation : '') 
        || '';

      console.groupCollapsed('[LoanApp][Prefill] corporate mapping');
      console.log('corporate.representativeOccupation:', corporate.representativeOccupation);
      console.log('fallback firstDirector.designation:', firstDirector ? (firstDirector as any).designation : undefined);
      console.log('final representativeOccupation used:', representativeOccupation);
      console.groupEnd();

      formData.company = {
        companyName: corporate.registeredName || '',
        companyRegNumber: corporate.registrationNumber || '',
        legalEntityType: (corporate as any).legalEntityType || '',
        registrationNumberType: (corporate as any).registrationNumberType || '',
        contactPersonFirstName,
        contactPersonLastName,
        // The representative as recorded on the customer, so a returning client is not re-asked.
        representativeIdNumber: (corporate as any).representativeIdNumber || '',
        representativeCapacity: (corporate as any).representativeCapacity || '',
        representativeMandate: (corporate as any).representativeMandate || '',
        contactTel: normalizeSouthAfricanPhoneNumber(phoneContact?.contactValue) || '',
        contactEmail: emailContact?.contactValue || '',
        address: {
          type: 'BUSINESS',
          lineOne: address?.streetAddress || '',
          lineTwo: address?.lineTwo || '',
          suburb: address?.suburb || '',
          province: address?.stateProvince || '',
          city: address?.city || '',
          code: address?.postalCode || ''
        },
        taxNumber: corporate.taxNumber || '',
        vatNumber: corporate.vatNumber || '',
        representativeOccupation: representativeOccupation,
        turnover: corporate.annualTurnover || 0,
        reasonForFunding: '',
        industrySector: corporate.industrySector || '',
        incomeType: corporate.incomeType || undefined,
        incomeDescription: corporate.incomeDescription || '',
        industryCategory: corporate.industryCategory || undefined,
        industryDescription: corporate.industryDescription || '',
        taxPin: corporate.taxPin || '',
        directors
      };
    }

    // Initialize financial data with 0.00 values
    formData.financial = {
      income: createDefaultFinancialObject(financialCategories.income),
      expenses: createDefaultFinancialObject(financialCategories.expenses),
      household: createDefaultFinancialObject(financialCategories.household),
      ...(customerType === 'individual' ? { otherLivingExpenses: 0, maintenance: 0 } : {})
    };

    // For bank account, use customer's bank details if available
    if (customer.bankDetails && customer.bankDetails.length > 0) {
      const bankDetail = customer.bankDetails[0];
      // Map string account type to AccountType enum value as string
      let accountType = 'SAVINGS';
      if (bankDetail.accountType) {
        if (bankDetail.accountType.includes('CURRENT') || bankDetail.accountType.includes('CHEQUE')) {
          accountType = 'CURRENT_CHEQUE';
        } else if (bankDetail.accountType.includes('TRANSMISSION')) {
          accountType = 'TRANSMISSION';
        } else if (bankDetail.accountType.includes('BUSINESS')) {
          accountType = 'BUSINESS';
        }
      }

      formData.bankAccount = {
        accountHolderName: bankDetail.accountHolderName || '',
        bankName: normalizeBankName(bankDetail.bankName || ''),
        accountNumber: bankDetail.accountNumber || '',
        accountType: accountType as AccountTypeString,
        branchCode: bankDetail.branchCode || ''
      };
    } else {
      // Default bank account details
      formData.bankAccount = {
        accountHolderName: '',
        bankName: '',
        accountNumber: '',
        accountType: 'SAVINGS' as AccountTypeString,
        branchCode: ''
      };
    }

    // Update form values with customer data
    methods.reset(formData);
  };

  // Function to populate form with lead data
  const populateFormWithLeadData = (lead: LoanLead) => {
    // The lead's loan type decides the customer: a personal loan is for an individual, a business
    // loan for a company. Leads captured before the type was chosen have none; for those, fall
    // back to whether a company was named.
    const customerType = lead.loanType === 'PERSONAL'
      ? 'individual'
      : lead.loanType === 'BUSINESS' || lead.registeredName ? 'company' : 'individual';

    // Create a base form data object with the common fields
    const formData: any = {
      customerType: customerType,
      loanType: customerType === 'company' ? 'BUSINESS' : 'PERSONAL',
    };

    // Add individual data if customer type is individual
    if (customerType === 'individual') {
      formData.individual = {
        fullNames: `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
        firstName: lead.firstName || '',
        lastName: lead.lastName || '',
        idNumber: lead.idNumber || '',
        contactDetails: {
          primaryPhone: normalizeSouthAfricanPhoneNumber(lead.primaryPhone) || '',
          email: lead.email || '',
          secondaryPhone: ''
        },
        address: {
          streetAddress: lead.physicalAddress || '',
          city: '',
          province: '',
          postalCode: ''
        },
        maritalStatus: 'SINGLE',
        occupation: lead.representativeOccupation || '',
        employer: '',
        employmentType: '',
        dateOfBirth: '',
        monthlyNetIncome: 0,
        dependents: 0
      };
    }
    // Add company data if customer type is company
    else {
      formData.company = {
        companyName: lead.registeredName || '',
        companyRegNumber: lead.registrationNumber || '',
        // Carried from the lead so a trust, practice or NPO survives the conversion instead of
        // being re-assumed to be a (Pty) Ltd with a CIPC number.
        legalEntityType: lead.legalEntityType || '',
        registrationNumberType: lead.registrationNumberType || '',
        contactPersonFirstName: lead.firstName || '',
        contactPersonLastName: lead.lastName || '',
        contactTel: normalizeSouthAfricanPhoneNumber(lead.primaryPhone) || '',
        contactEmail: lead.email || '',
        address: {
          type: 'BUSINESS',
          // Website captures the business address as a single line; prefill line one.
          lineOne: lead.physicalAddress || '',
          lineTwo: '',
          suburb: '',
          province: '',
          city: '',
          code: ''
        },
        taxNumber: lead.taxNumber || '',
        vatNumber: lead.vatNumber || '',
        representativeOccupation: lead.representativeOccupation || '',
        turnover: lead.turnover || 0,
        reasonForFunding: '',
        industrySector: lead.industrySector || '',
        // Funding option from the website (e.g. PO) maps to the company income type.
        incomeType: (lead.incomeType as any) || undefined,
        incomeDescription: '',
        industryCategory: undefined,
        industryDescription: '',
        taxPin: '',
        directors: [
          {
            firstName: lead.firstName || '',
            lastName: lead.lastName || '',
            // A sole proprietor's registration number IS the owner's SA ID (see legalEntityType.ts).
            idNumber: lead.idNumber || (lead.legalEntityType === 'SOLE_PROPRIETOR' ? lead.registrationNumber || '' : ''),
            nationality: '',
            dateOfBirth: '',
            designation: lead.designation || '',
            ownershipPercentage: lead.ownershipPercentage || 0,
            isExecutive: false,
            appointmentDate: '',
            gender: undefined,
            isDisabled: false,
            isPreviouslyDisadvantaged: false,
            race: undefined,
            addresses: []
          }
        ]
      };
    }

    // Initialize financial data with 0.00 values
    formData.financial = {
      income: createDefaultFinancialObject(financialCategories.income),
      expenses: createDefaultFinancialObject(financialCategories.expenses),
      household: createDefaultFinancialObject(financialCategories.household),
      ...(customerType === 'individual' ? { otherLivingExpenses: 0, maintenance: 0 } : {})
    };

    // Add loan details
    formData.loan = {
      loanAmount: lead.loanAmount ?? (customerType === 'individual' ? undefined : 0),
      loanPurpose: '',
      loanTerm: 12,
      loanType: 'PAYDAY', // Default loan type
      securityType: '' as any,
      customLoanType: '' as any,
      interestRate: customerType === 'individual' ? undefined : 0,
      adminFee: customerType === 'individual' ? undefined : 0,
      totalRepayment: customerType === 'individual' ? undefined : 0,
      interestAmount: customerType === 'individual' ? undefined : 0,
      dueDate: '',
      commencementDate: ''
    };

    // Add bank account details
    formData.bankAccount = {
      accountHolderName: lead.registeredName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
      bankName: normalizeBankName(lead.bankName || ''),
      accountNumber: '',
      accountType: 'CURRENT_CHEQUE' as AccountTypeString,
      branchCode: ''
    };

    // Update form values with lead data
    methods.reset(formData);
  };

  // Fetch lead data when component mounts with taskId
  useEffect(() => {
    const handleLeadData = (data: LoanLead) => {
      console.log('Setting lead data:', data);
      setLeadData(data);
      populateFormWithLeadData(data);
      setIsLoadingLead(false);
      // Lead conversion: skip Customer Type + Customer Search, start at Customer Info (step 3)
      setIsLeadConversion(true);
      setActiveStep(2);
    };

    const fetchLeadData = () => {
      if (!taskId || isLoadingLead || leadData) {
        return;
      }

      console.log('Fetching lead data for taskId:', taskId);
      setIsLoadingLead(true);
      findLeadByTaskMutation.mutate({ taskId });
    };

    // Check for stored lead data first
    if (!taskId && !leadData) {
      const storedLeadData = sessionStorage.getItem('leadData');
      if (storedLeadData) {
        try {
          const parsedLeadData = JSON.parse(storedLeadData);
          console.log('Found lead data in session storage:', parsedLeadData);
          handleLeadData(parsedLeadData);
          sessionStorage.removeItem('leadData');
          return;
        } catch (error) {
          console.error('Error parsing lead data from session storage:', error);
        }
      }
    }

    // Fetch lead data if needed
    fetchLeadData();
  }, [taskId, findLeadByTaskMutation.data, isLoadingLead, leadData, populateFormWithLeadData]);

  // Handle window resize
  const handleResize = () => {
    setWindowDimension({ width: window.innerWidth, height: window.innerHeight });
  };

  // Add resize event listener
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add effect to handle submission control
  useEffect(() => {
    // If we're at the review step
    if (activeStep === 7) {
      // Mark that we just arrived at the review step
      setJustArrivedAtReviewStep(true);
      // Initially disallow submission
      setAllowSubmission(false);

      // Set a timeout to allow submission after a delay
      const timer = setTimeout(() => {
        setJustArrivedAtReviewStep(false);
        setAllowSubmission(true);
      }, 1000); // 1 second delay

      return () => {
        clearTimeout(timer);
      };
    } else {
      // Reset flags when not on review step
      setJustArrivedAtReviewStep(false);
      setAllowSubmission(false);
    }
  }, [activeStep]);

  // Define steps
  const steps = [
    { label: 'Customer Type', completed: activeStep > 0, index: 0 },
    { label: 'Customer Search', completed: activeStep > 1, index: 1 },
    { label: 'Customer Info', completed: activeStep > 2, index: 2 },
    { label: 'Loan / Funding Request', completed: activeStep > 3, index: 3 },
    { label: 'Funding Details', completed: activeStep > 4, index: 4 },
    { label: 'Bank Details', completed: activeStep > 5, index: 5 },
    { label: 'Collateral', completed: activeStep > 6, index: 6 },
    { label: 'Review', completed: activeStep > 7, index: 7 }
  ];

  // Get step title function
  const getStepTitle = (step: number): string => {
    if (methods.getValues('loanType') === 'PERSONAL') {
      return individualJourneySteps.find(item => item.index === step)?.label || 'Unknown Step';
    }
    return steps[step]?.label || 'Unknown Step';
  };

  // Initialize form
  const methods = useForm<FormValues>({
    resolver: zodResolver(loanApplicationSchema),
    mode: 'onChange',
    // Keep field values when steps unmount so going back doesn't lose edits
    shouldUnregister: false,
    defaultValues: {
      loanType: !isResumingSavedDraft && navState.customerType === 'individual' ? 'PERSONAL' : 'BUSINESS',
      customerType: !isResumingSavedDraft && navState.customerType === 'individual' ? 'individual' : 'company',
      individual: {
        fullNames: '',
        firstName: '',
        lastName: '',
        idNumber: '',
        maritalStatus: 'SINGLE',
        occupation: '',
        employer: '',
        employmentType: '',
        dateOfBirth: '',
        address: {
          streetAddress: '',
          city: '',
          province: '',
          postalCode: ''
        },
        contactDetails: {
          primaryPhone: '',
          email: ''
        },
        dependents: 0
      },
      company: {
        companyName: '',
        companyRegNumber: '',
        legalEntityType: '',
        registrationNumberType: '',
        representativeIdNumber: '',
        representativeCapacity: '',
        representativeMandate: '',
        taxNumber: '',
        vatNumber: '',
        contactPersonFirstName: '',
        contactPersonLastName: '',
        contactTel: '',
        contactEmail: '',
        address: {
          type: 'BUSINESS',
          lineOne: '',
          lineTwo: '',
          suburb: '',
          province: '',
          city: '',
          code: ''
        },
        representativeOccupation: '',
        turnover: 0,
        reasonForFunding: '',
        industrySector: '',
        incomeType: undefined,
        incomeDescription: '',
        industryCategory: undefined,
        industryDescription: '',
        taxPin: '',
        directors: [{ 
          firstName: '', 
          lastName: '', 
          idNumber: '', 
          nationality: '', 
          dateOfBirth: '', 
          designation: '', 
          ownershipPercentage: 0, 
          isExecutive: false, 
          appointmentDate: '', 
          gender: undefined, 
          isDisabled: false, 
          isPreviouslyDisadvantaged: false, 
          race: undefined, 
          addresses: [] 
        }]
      },
      financial: {
        income: createDefaultFinancialObject(financialCategories.income),
        expenses: createDefaultFinancialObject(financialCategories.expenses),
        household: createDefaultFinancialObject(financialCategories.household),
        hasExistingCreditObligations: false
      },
      bankAccount: {
        accountHolderName: '',
        bankName: '',
        accountNumber: '',
        accountType: 'SAVINGS' as AccountTypeString,
        branchCode: ''
      },
      loan: {
        calculationMode: undefined as LoanCalculationModeType | undefined,
        loanProductType: undefined,
        customLoanType: '' as any,
        loanAmount: 0,
        loanTerm: 12,
        loanPurpose: '',
        securityType: '' as any,
        interestRate: 0,
        adminFee: 0,
        totalRepayment: 0,
        interestAmount: 0,
        dueDate: '',
        commencementDate: '',
        numberOfInstallments: 1,
        fundingType: '',
        projectClientName: '',
        projectDescription: '',
        projectReferenceNumber: '',
        projectValue: 0,
        projectExpectedCompletionDate: '',
        projectExpectedPaymentDate: ''
      },
      collaterals: []
    }
  });
  const quotationState = methods.watch('loan.individualQuotationState');
  useEffect(() => {
    if (quotationState === 'ready') setStepValidationMessages(messages => messages.filter(message => !message.toLowerCase().includes('quotation')));
  }, [quotationState]);
  // Saved automatically and explicitly; server drafts are resumable from Pending Submissions.
  const { saveStatus: draftSaveStatus, lastSavedAt: draftSavedAt, isResuming: isResumingDraft, resumeError: draftResumeError, removeDraft, saveDraft } = useWizardDraftPersistence({
    methods,
    activeStep,
    setActiveStep,
    statementDraftId,
    setStatementDraftId,
    isExistingCustomer,
    setIsExistingCustomer,
    isLeadConversion,
    setIsLeadConversion,
    isUnsecuredLoan,
    setIsUnsecuredLoan,
    isSubmitSuccess,
    serverDraftsEnabled: !location.pathname.startsWith('/loan-application/edit'),
    stepLabel: getStepTitle(activeStep),
    savedBy: authUser?.name || authUser?.username || undefined,
    leadData,
    setLeadData,
    customerData,
    setCustomerData
  });

  const watchedCommencementDate = methods.watch('loan.commencementDate');
  const presentationRoot = useRef<HTMLDivElement>(null);
  const presentationLoanType = methods.watch('loanType');
  useEffect(() => {
    resetIndividualStepView(presentationRoot.current, presentationLoanType);
  }, [activeStep, presentationLoanType]);
  const watchedDueDate = methods.watch('loan.dueDate');

  const isLoanDateRangeInvalid = useMemo(() => {
    if (!watchedCommencementDate || !watchedDueDate) {
      return false;
    }
    return new Date(watchedDueDate) < new Date(watchedCommencementDate);
  }, [watchedCommencementDate, watchedDueDate]);

  // Initialize the mutation
  const createLoanApplicationMutation = useCreateLoanApplication({
    mutation: {
      onSuccess: async (data) => {
        if (data?.customerPartyId && methods.getValues('customerType') === 'individual') {
          try { await checkSavedIndividualIdentity(data.customerPartyId, individualCreditAssessment?.result.report?.id); }
          catch { toast.warning('Application saved. Check XDS identity from the customer profile.'); }
        }
        if (data?.id && methods.getValues('customerType')==='individual') {
          sessionStorage.removeItem(STATEMENT_DRAFT_KEY);
          clearPersistedWizardState();
          setStatementDraftId(undefined);
        }
        console.log('Application submitted successfully:', data);
        
        // Submit collected comments if any
        if (collectedComments.length > 0 && data?.id) {
          try {
            console.log('Submitting collected comments:', collectedComments);
            for (const comment of collectedComments) {
              await addCommentMutation.mutateAsync({
                loanApplicationId: data.id,
                comment: comment,
                createdBy: authUser?.name || authUser?.username || undefined,
              });
            }
            console.log('All collected comments submitted successfully');
          } catch (commentError) {
            console.error('Error submitting collected comments:', commentError);
            // Don't fail the whole submission for comment errors
          }
        }
        
        setIsLoading(false);
        setIsSubmitSuccess(true);
        void removeDraft();
        console.log('Setting isSubmitSuccess to true');
        if (data && data.applicationNumber) {
          setApplicationNumber(data.applicationNumber);
          console.log('Application number set:', data.applicationNumber);
        }
        if (data && data.id) {
          setSubmittedApplicationId(data.id);
          console.log('Application ID set:', data.id);
        }
      },
      onError: (error: any) => {
        submitInFlight.current = false;
        setIsLoading(false);
        setError(error?.response?.data?.message || error?.message || 'Failed to submit application. Please try again.');
        console.error('Error submitting application:', error);
      }
    }
  });

  // Comment mutation for submitting collected comments
  const addCommentMutation = useAddComment();

  const handleFullScreen = () => setClientFullScreen(!clientFullScreen);

  // Function to search for customer
  const handleCustomerSearch = (data: { registrationNumber?: string; idNumber?: string; }) => {
    setError(null);
    if (!data.registrationNumber && !data.idNumber) {
      setError('Please enter either a registration number or ID number to search');
      return;
    }

    // Clear previous search results when starting a new search
    setIsExistingCustomer(null);
    setCustomerData(null);

    // Update search parameters and trigger search
    setSearchParams(data);
    setIsSearching(true); // Set isSearching to true to trigger the API call
  };

  // Function to start new application with no customer data
  const handleStartNewApplication = () => {
    // Reset the form to default values
    methods.reset();
    clearPersistedWizardState();
    sessionStorage.removeItem(STATEMENT_DRAFT_KEY);
    setStatementDraftId(undefined);
    setCustomerData(null);
    setIsExistingCustomer(false);
    setIndividualCreditAssessment(null);
    // Set to customer info step (step 2)
    setActiveStep(2);
  };

  // Function to continue with existing customer data
  const handleContinueWithCustomer = () => {
    if (customerData) {
      // Populate form with customer data
      populateFormWithCustomerData(customerData);
    }
    // Set to customer info step (step 2)
    setActiveStep(2);
  };

  // Function to handle customer selection from search results
  const handleCustomerSelect = (customer: Customer) => {
    if (isRestrictedCustomer(customer.status)) {
      showCustomerRestrictionToast(customer.status);
      return;
    }
    setCustomerData(customer);
    setIsExistingCustomer(true);
    setIndividualCreditAssessment(null);
    populateFormWithCustomerData(customer);
    // Move to customer info step (step 2)
    setActiveStep(2);
  };

  // Function to handle skipping customer search
  const handleSkipCustomerSearch = () => {
    setCustomerData(null);
    setIsExistingCustomer(false);
    setIndividualCreditAssessment(null);
    // Move to customer info step (step 2) without customer data
    setActiveStep(2);
  };

  const handleNext = async () => {
    setStepValidationMessages([]);
    setValidationAttempt(value => value + 1);
    try {
      // Validate only the current step fields
      let result = false;
      let actionMessage = '';

      // Company request stays in slot 3; individual amounts move to slot 9.
      const validationStep = activeStep === 3 && methods.getValues('loanType') === 'PERSONAL'
        ? 'income' : activeStep === 9 ? 3 : activeStep;
      switch (validationStep) {
        case 0:
          result = await methods.trigger('customerType');
          break;
        case 1:
          // Customer search step - no validation needed, user can proceed or skip
          result = true;
          break;
        case 2:
          // Validate individual or company info based on customer type
          const customerType = methods.getValues('customerType');
          if (customerType === 'individual') {
            result = await methods.trigger(['individual.fullNames', 'individual.firstName', 'individual.lastName',
              'individual.idNumber', 'individual.idType', 'individual.passportExpiryDate', 'individual.dateOfBirth', 'individual.maritalStatus', 'individual.dependents']);
          } else {
            result = await methods.trigger('company');
          }
          break;
        case 'income': {
          result = await methods.trigger(['individual.monthlyNetIncome', 'individual.monthlyLivingExpenses', 'financial']);
          const breakdownIssues = individualBreakdownIssues(methods.getValues('financial'));
          breakdownIssues.forEach(issue => methods.setError(`financial.individualBreakdown.${issue.name}` as any, {type: 'validate', message: issue.message}));
          if (breakdownIssues.length) result = false;
          const earlyIncomeIssues = individualFinancialErrors(methods.getValues('individual'));
          earlyIncomeIssues.forEach(({field, message}) => methods.setError(`individual.${field}` as any, {type: 'validate', message}));
          if (earlyIncomeIssues.length) result = false;
          const grossIssue = individualGrossIncomeIssue(capturedAffordability(methods.getValues()));
          if (grossIssue) {
            actionMessage = grossIssue;
            result = false;
          }
          break;
        }
        case 3:
          // Trigger validation for all loan fields
          result = await methods.trigger('loan');

          // Special handling for due date validation
          const customLoanType = methods.getValues('loan.customLoanType');
          const dueDate = methods.getValues('loan.dueDate');
          if (methods.getValues('loanType') === 'PERSONAL') {
            const breakdownIssues = individualBreakdownIssues(methods.getValues('financial'));
            breakdownIssues.forEach(issue => methods.setError(`financial.individualBreakdown.${issue.name}` as any, {type: 'validate', message: issue.message}));
            if (breakdownIssues.length) result = false;
            const financialValid = await methods.trigger(['individual.monthlyNetIncome', 'individual.monthlyLivingExpenses', 'financial']);
            const incomeIssues = individualFinancialErrors(methods.getValues('individual'));
            incomeIssues.forEach(({field, message}) => methods.setError(`individual.${field}` as any, {type: 'validate', message}));
            if (!financialValid || incomeIssues.length) result = false;
            const issues = individualProductErrors(methods.getValues('loan'));
            const quotationRequired = individualQuotationRequired(methods.getValues('loan'));
            if (quotationRequired) issues.unshift({ field: 'individualPricingQuote', message: quotationRequired });
            issues.forEach(({field, message}) => methods.setError(`loan.${field}` as any, { type: 'validate', message }));
            if (issues.length) result = false;
          }

          if (customLoanType === 'ONCE_OFF' && !dueDate) {
            if (methods.getValues('loanType') !== 'PERSONAL') toast.error('Due date is required for this loan type.');
            // Force validation error to appear
            methods.setError('loan.dueDate', {
              type: 'required',
              message: 'Due date is required'
            });
            result = false;
          }
          break;
        case 4:
          // Individuals validate their delayed contact/address capture here.
          // Company product-specific funding detail remains optional at origination.
          result = methods.getValues('loanType') === 'PERSONAL'
            ? await methods.trigger(['individual.contactDetails', 'individual.address']) : true;
          break;
        case 8:
          result = qualificationReady;
          if (!result) actionMessage = 'Complete affordability qualification before entering the loan request. Review the missing figures and bureau repayment confirmation on this page.';
          break;
        case 5:
          result = await methods.trigger('bankAccount');
          if (methods.getValues('loanType') === 'PERSONAL') {
            const profileIssues = individualIncomeErrors(methods.getValues('individual'));
            const maritalError = maritalStatusError(methods.getValues('individual.maritalStatus'));
            if (maritalError) {
              methods.setError('individual.maritalStatus',{type:'validate',message:maritalError});
              result=false;
            }
            profileIssues.forEach(({field, message}) => methods.setError(`individual.${field}` as any, {type: 'validate', message}));
            if (profileIssues.length) result = false;
          }
          break;
        case 6:
          if (methods.getValues('loanType') === 'PERSONAL') {
            result = await methods.trigger(['individual.employmentType', 'individual.occupation', 'individual.employer', 'individual.incomeSource']);
            const employmentIssues = individualEmploymentErrors(methods.getValues('individual'));
            employmentIssues.forEach(({field, message}) => methods.setError(`individual.${field}` as any, {type: 'validate', message}));
            if (employmentIssues.length) result = false;
            break;
          }
          // Skip validation if user chose not to provide collateral
          const hasCollateral = document.getElementById('hasCollateral') as HTMLInputElement;
          if (hasCollateral && !hasCollateral.checked) {
            result = true;
          } else {
            result = await methods.trigger('collaterals');
          }
          break;
        default:
          result = true;
          break;
      }

      console.log('Validation result:', result);

      if (!result) {
        if (methods.getValues('loanType') === 'PERSONAL') {
          const sections = validationStep === 3 ? ['loan', 'financial', 'individual.monthlyNetIncome', 'individual.monthlyLivingExpenses']
            : validationStep === 'income' ? ['financial', 'individual.monthlyNetIncome', 'individual.monthlyLivingExpenses']
            : validationStep === 5 ? ['bankAccount', 'individual'] : ['individual'];
          const messages = sections.flatMap(section => individualValidationMessages(
            methods.getFieldState(section as any).error, section.split('.')));
          setStepValidationMessages(actionMessage ? [actionMessage, ...messages]
            : messages.length ? messages : ['Review the errors on this step before continuing.']);
        }
        console.log('Validation failed, form errors:', methods.formState.errors);
        return;
      }

      // The security type is chosen on the funding request step (step 3), so that is where we
      // decide whether the Collateral step applies.
      if (methods.getValues('loanType') === 'PERSONAL') {
        setActiveStep(individualNextStep(activeStep));
        return;
      }
      if (activeStep === 3) {
        // Check if the security type is UNSECURED
        const securityTypeSelect = document.getElementById('securityType') as HTMLSelectElement;
        if (securityTypeSelect && securityTypeSelect.value === 'UNSECURED') {
          // Store the security type in state
          setIsUnsecuredLoan(true);
          // Still capture product detail and banking, just skip Collateral on the way to Review.
          setActiveStep(4);
          console.log('Loan is UNSECURED - the Collateral step will be skipped.');
          return;
        } else {
          // If not unsecured, make sure state is updated
          setIsUnsecuredLoan(false);
        }
      }

      // Bank Details (step 5) leads straight to Review when the loan is unsecured.
      if (activeStep === 5 && isUnsecuredLoan) {
        setActiveStep(7);
        console.log('Skipping Collateral step because loan is UNSECURED. Moving to Review step.');
        return;
      }

      // Move to next step
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      console.log('Moving to step:', activeStep + 1);
    } catch (err) {
      console.error('Error in handleNext:', err);
      setError('An error occurred. Please try again.');
    }
  };

  const handleBack = () => {
    if (methods.getValues('loanType') === 'PERSONAL') {
      if (isLeadConversion && activeStep <= 2) return;
      setActiveStep(individualPreviousStep(activeStep));
      return;
    }
    // If we're at the Review step (step 7) and we previously determined the loan is unsecured,
    // skip back over the Collateral step to Bank Details (step 5)
    if (activeStep === 7 && isUnsecuredLoan) {
      setActiveStep(5);
      return;
    }

    // During lead conversion the Customer Type + Customer Search steps are skipped,
    // so never allow navigating back before Customer Info (step 3 / index 2).
    if (isLeadConversion && activeStep <= 2) {
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async (data: FormValues) => {
    // Prevent submission if it's not explicitly allowed
    if (justArrivedAtReviewStep || !allowSubmission) {
      console.log('Submission prevented: Just arrived at review step or submission not allowed yet');
      return;
    }
    if (submitInFlight.current || isSubmitSuccess) {
      console.log('Submission prevented: an application is already being submitted or was submitted');
      return;
    }
    // The Review submit controls call this directly, so enforce individual capture here too.
    if (data.loanType === 'PERSONAL') {
      const maritalError = maritalStatusError(data.individual?.maritalStatus);
      if (maritalError) {
        methods.setError('individual.maritalStatus',{type:'validate',message:maritalError});
        toast.error(maritalError); setActiveStep(2); return;
      }
      const profileIssues = individualIncomeErrors(data.individual);
      const productIssues = individualProductErrors(data.loan);
      profileIssues.forEach(({field, message}) => methods.setError(`individual.${field}` as any, {type: 'validate', message}));
      productIssues.forEach(({field, message}) => methods.setError(`loan.${field}` as any, {type: 'validate', message}));
      if (profileIssues.length || productIssues.length) {
        toast.error((profileIssues[0] || productIssues[0]).message);
        setActiveStep(profileIssues.length ? (profileIssues[0].field === 'dateOfBirth' ? 2
          : individualEmploymentErrors(data.individual).length ? 6 : 3) : 9);
        return;
      }
    }
    submitInFlight.current = true;

    try {
      setIsLoading(true);
      setError(null);

      console.log('Form data to submit:', data);
      console.log('Lead data available:', leadData);
      console.log('Lead ID:', leadData?.id);

      // Create the loan application object
      const loanApplication: LoanApplication = {
        loanType: data.loanType as LoanApplicationType,
        loanLeadId: leadData?.id // Directly assign the lead ID
      } as LoanApplication;

      console.log('Created loan application:', loanApplication);

      // Add customer ID if available
      if (customerData?.id) {
        (loanApplication as any).partyId = customerData.id;
      }

      // Add individual or company details based on customer type
      if (data.customerType === 'individual' && data.individual) {
        loanApplication.individual = {
          fullNames: data.individual.fullNames,
          firstName: data.individual.firstName,
          lastName: data.individual.lastName,
          residentialAddressDetails: {
            streetAddress: data.individual.address.streetAddress,
            city: data.individual.address.city,
            stateProvince: data.individual.address.province,
            postalCode: data.individual.address.postalCode,
            country: data.individual.address.country
          },
          // Customer ID: the customer service stores the customer number; send what is known (or a placeholder)
          idNumber: usesCustomerIdentity(data.individual.idType) ? (data.individual.idNumber || 'CUSTOMER_ID').toUpperCase()
            : data.individual.idType === 'PASSPORT' ? data.individual.idNumber.toUpperCase() : data.individual.idNumber,
          idType: data.individual.idType || 'SA_ID',
          passportExpiryDate: data.individual.idType === 'PASSPORT' ? data.individual.passportExpiryDate : undefined,
          maritalStatus: data.individual.maritalStatus as MaritalStatus,
          occupation: data.individual.occupation,
          ...incomeProfileRequest(data.individual),
          residentialAddress: `${data.individual.address.streetAddress}, ${data.individual.address.city}, ${data.individual.address.province}, ${data.individual.address.postalCode}`,
          cellNumber: data.individual.contactDetails.primaryPhone,
          email: data.individual.contactDetails.email,
          // Add required fields with default values
          employerAddress: '',
          periodEmployed: '',
          nextOfKin: '',
          nextOfKinTel: '',
          reasonForLoan: data.loan?.loanPurpose || ''
        };
      } else if (data.customerType === 'company' && data.company) {
        loanApplication.company = {
          companyName: data.company.companyName,
          companyRegNumber: data.company.companyRegNumber,
          // zod validates this as a string; the generated Company type narrows it to the
          // enum union. The value can only be one of the dropdown options.
          legalEntityType: data.company.legalEntityType as CompanyLegalEntityType,
          taxNumber: data.company.taxNumber || '',
          vatNumber: data.company.vatNumber,
          contactPersonFirstName: data.company.contactPersonFirstName,
          contactPersonLastName: data.company.contactPersonLastName,
          contactTel: data.company.contactTel,
          contactEmail: data.company.contactEmail,
          address: data.company.address as CompanyAddress,
          representativeOccupation: data.company.representativeOccupation,
          turnover: data.company.turnover,
          reasonForFunding: data.loan?.loanPurpose || '',
          // Include new corporate fields if they exist
          ...(data.company.industrySector && { industrySector: data.company.industrySector }),
          ...(data.company.incomeType && { incomeType: data.company.incomeType }),
          ...(data.company.incomeDescription && { incomeDescription: data.company.incomeDescription }),
          ...(data.company.industryCategory && { industryCategory: data.company.industryCategory }),
          ...(data.company.industryDescription && { industryDescription: data.company.industryDescription }),
          ...(data.company.taxPin && { taxPin: data.company.taxPin }),
          // Send the full director record, not just the identifying fields. The static details
          // below live on the customer master, so dropping them here is what forced the user to
          // re-capture them on every subsequent application for the same customer.
          ...(data.company.registrationNumberType
            ? { registrationNumberType: data.company.registrationNumberType as RegistrationNumberType }
            : {}),
          // The contact person is the representative, so their name is what gets stored as
          // such. Until now it was discarded on save and rebuilt on read as "the first
          // director", which is how the person we deal with came to be misrecorded.
          representativeFirstName: data.company.contactPersonFirstName?.trim(),
          representativeLastName: data.company.contactPersonLastName?.trim(),
          representativeIdNumber: data.company.representativeIdNumber?.trim() || undefined,
          representativeMandate: data.company.representativeMandate?.trim() || undefined,
          ...(data.company.representativeCapacity
            ? { representativeCapacity: data.company.representativeCapacity as RepresentativeCapacity }
            : {}),
          directors: data.company.directors.map((director): CompanyDirector => ({
            firstName: director.firstName,
            lastName: director.lastName,
            idNumber: director.idNumber,
            ...(director.idType && { idType: director.idType }),
            ...(director.nationality && { nationality: director.nationality }),
            ...(director.dateOfBirth && { dateOfBirth: director.dateOfBirth }),
            ...(director.designation && { designation: director.designation }),
            ...(director.ownershipPercentage !== undefined && { ownershipPercentage: director.ownershipPercentage }),
            ...(director.isExecutive !== undefined && { isExecutive: director.isExecutive }),
            ...(director.appointmentDate && { appointmentDate: director.appointmentDate }),
            ...(director.gender && { gender: director.gender }),
            ...(director.isDisabled !== undefined && { isDisabled: director.isDisabled }),
            ...(director.isPreviouslyDisadvantaged !== undefined && { isPreviouslyDisadvantaged: director.isPreviouslyDisadvantaged }),
            ...(director.race && { race: director.race }),
            ...(director.email?.trim() && { email: director.email.trim() }),
            ...(director.cellNumber?.trim() && { cellNumber: director.cellNumber.trim() })
          }))
        };
      }

      // Add financial details if provided
      if (data.financial) {
        const capturedFinancial = data.customerType === 'individual' ? individualFinancialPayload(data.financial) : data.financial;
        const financialDetails = {
          income: capturedFinancial.income,
          expenses: capturedFinancial.expenses,
          household: capturedFinancial.household,
          hasExistingCreditObligations: capturedFinancial.hasExistingCreditObligations,
          ...(data.customerType === 'individual' ? { livingExpenses: data.individual?.monthlyLivingExpenses,
            maintenance: capturedFinancial.maintenance, otherLivingExpenses: capturedFinancial.otherLivingExpenses,
            individualBreakdown: capturedFinancial.individualBreakdown,
            ...(data.financial.otherLivingExpenseItems != null && { otherLivingExpenseItems: data.financial.otherLivingExpenseItems }) } : {})
        };
        loanApplication.financialDetails = financialDetails;
      }

      // Add bank account details if provided
      if (data.bankAccount) {
        loanApplication.bankAccount = {
          accountHolderName: data.bankAccount.accountHolderName,
          bankName: data.bankAccount.bankName,
          accountNumber: data.bankAccount.accountNumber,
          accountType: data.bankAccount.accountType,
          // Kept so disbursement can prefill it (#34)
          ...(data.bankAccount.branchCode?.trim() ? { branchCode: data.bankAccount.branchCode.trim() } : {})
        };
      }

      // Add loan details if provided
      if (data.loan) {
        loanApplication.loanDetails = {
          loanAmount: data.loan.loanAmount,
          loanTerms: data.loan.loanTerm,
          loanPurpose: data.loan.loanPurpose,
          securityType: data.loan.securityType,
          interestRate: data.loan.interestRate,
          adminFee: data.loan.adminFee,
          defaultInterestRate: data.loan.defaultInterestRate,
          defaultChargeFrequency: data.loan.defaultChargeFrequency,
          defaultChargeIntervalDays: data.loan.defaultChargeFrequency === 'CUSTOM_DAYS'
            ? data.loan.defaultChargeIntervalDays
            : undefined,
          defaultGraceDays: data.loan.defaultGraceDays,
          totalRepayment: data.loan.totalRepayment,
          interestAmount: data.loan.interestAmount,
          dueDate: data.loan.dueDate,
          commencementDate: data.loan.commencementDate,
          calculationMode: data.loan.calculationMode,
          customLoanType: data.loan.customLoanType,
          numberOfInstallments: data.loan.numberOfInstallments,
          // Type of Funding Requested + Project / Purchase-Order information
          fundingType: data.loan.fundingType || undefined,
          projectClientName: data.loan.projectClientName || undefined,
          projectDescription: data.loan.projectDescription || undefined,
          projectReferenceNumber: data.loan.projectReferenceNumber || undefined,
          projectValue: data.loan.projectValue,
          projectExpectedCompletionDate: data.loan.projectExpectedCompletionDate || undefined,
          projectExpectedPaymentDate: data.loan.projectExpectedPaymentDate || undefined,
          // Funding request captured at origination
          requiredFundingDate: data.loan.requiredFundingDate || undefined,
          proposedRepaymentDate: data.loan.proposedRepaymentDate || undefined,
          customerContribution: data.loan.customerContribution,
          primaryRepaymentSource: data.loan.primaryRepaymentSource || undefined,
          securityAvailability: data.loan.securityAvailability || undefined,
          securityTypesOffered: data.loan.securityTypesOffered?.length
            ? (data.loan.securityTypesOffered as OfferedSecurityType[])
            : undefined,
          // Invoice discounting
          invoiceAmount: data.loan.invoiceAmount,
          invoiceDate: data.loan.invoiceDate || undefined,
          invoiceDebtorName: data.loan.invoiceDebtorName || undefined,
          invoicePaymentTerms: data.loan.invoicePaymentTerms || undefined,
          invoiceExpectedPaymentDate: data.loan.invoiceExpectedPaymentDate || undefined,
          invoiceAmountRequested: data.loan.invoiceAmountRequested,
          // Bridge funding
          bridgeUnderlyingTransaction: data.loan.bridgeUnderlyingTransaction || undefined,
          bridgeExpectedIncomingAmount: data.loan.bridgeExpectedIncomingAmount,
          bridgeSourceOfExpectedPayment: data.loan.bridgeSourceOfExpectedPayment || undefined,
          bridgePayerName: data.loan.bridgePayerName || undefined,
          bridgeReasonForFundingGap: data.loan.bridgeReasonForFundingGap || undefined,
          bridgeExpectedPaymentDate: data.loan.bridgeExpectedPaymentDate || undefined,
          // Purchase order funding
          poCostToFulfil: data.loan.poCostToFulfil,
          supplierName: data.loan.supplierName || undefined,
          // Invoice discounting (additional)
          invoiceGoodsDescription: data.loan.invoiceGoodsDescription || undefined,
          invoiceStatus: data.loan.invoiceStatus || undefined,
          // Working capital
          workingCapitalMonthlyTurnover: data.loan.workingCapitalMonthlyTurnover,
          workingCapitalOperatingCycleDays: data.loan.workingCapitalOperatingCycleDays,
          workingCapitalCurrentDebtors: data.loan.workingCapitalCurrentDebtors,
          workingCapitalCurrentCreditors: data.loan.workingCapitalCurrentCreditors,
          // Project finance (additional)
          projectTotalCost: data.loan.projectTotalCost,
          projectStartDate: data.loan.projectStartDate || undefined,
          projectPaymentMilestones: data.loan.projectPaymentMilestones || undefined,
          projectExpectedFirstPaymentDate: data.loan.projectExpectedFirstPaymentDate || undefined,
          // Equipment / vehicle (asset) funding
          assetDescription: data.loan.assetDescription || undefined,
          assetPurchasePrice: data.loan.assetPurchasePrice,
          assetPurpose: data.loan.assetPurpose || undefined,
          assetExpectedMonthlyIncome: data.loan.assetExpectedMonthlyIncome,
          assetUsefulLifeMonths: data.loan.assetUsefulLifeMonths,
          assetInsuranceStatus: data.loan.assetInsuranceStatus || undefined
        };
      }


      // Add collaterals if provided
      if (data.loan?.securityType === 'SECURED' && data.collaterals && data.collaterals.length > 0) {
        loanApplication.collaterals = data.collaterals.map(collateral => ({
          assetDescription: collateral.assetDescription,
          make: collateral.make,
          yearModel: collateral.yearModel,
          registrationNumber: collateral.registrationNumber,
          otherDetails: collateral.otherDetails,
          ownerName: collateral.ownerName,
          ownerId: collateral.ownerId,
          value: collateral.value,
          ...(collateral.mileageKm !== undefined && !Number.isNaN(collateral.mileageKm) ? { mileageKm: collateral.mileageKm } : {}),
          ...(collateral.retailValue !== undefined && !Number.isNaN(collateral.retailValue) ? { retailValue: collateral.retailValue } : {}),
          ...(collateral.retailValueSource?.trim() ? { retailValueSource: collateral.retailValueSource.trim() } : {}),
          ...(typeof collateral.insured === 'boolean' ? { insured: collateral.insured } : {}),
          ...(collateral.insured && collateral.insurer?.trim() ? { insurer: collateral.insurer.trim() } : {}),
          ...(collateral.insured && collateral.insurancePolicyNumber?.trim() ? { insurancePolicyNumber: collateral.insurancePolicyNumber.trim() } : {})
        }));
      } else {
        // For unsecured loans, don't include collaterals
        loanApplication.collaterals = undefined;
      }

      console.log('Submitting loan application:', loanApplication);

      // Submit the application. No client quotation payload/rates are trusted by the binding endpoint.
      if (data.customerType === 'individual' && data.loan.individualPricingQuote) Object.assign(loanApplication, { individualPricingQuoteId: data.loan.individualPricingQuote.quoteId });
      if(data.customerType==='individual' && statementCaptureApplies(data.individual?.idType) && statementDraftId) Object.assign(loanApplication,{statementCaptureDraftId:statementDraftId});
      createLoanApplicationMutation.mutate({
        data: loanApplication
      });

    } catch (error) {
      console.error('Error in handleSubmit:', error);
      submitInFlight.current = false;
      setIsLoading(false);

      if (!(error instanceof Error && error.message.includes('timeout'))) {
        setError('An error occurred while submitting your application. Please try again.');
      }
    }
  };

  const getStepContent = (step: number) => {
    const customerType = methods.watch('customerType');
    
    switch (step) {
      case 0:
        return <CustomerTypeStep />;
      case 1:
        return (
          <NewCustomerSearchStep
            onSelect={handleCustomerSelect}
            onSkip={handleSkipCustomerSearch}
          />
        );
      case 2:
        // Show individual or company info based on customer type
        if (customerType === 'individual') {
          return (
            <><IndividualInfoStep section="identity" idReadOnly={!!customerIdentity.data?.idLocked || (!!customerData?.id && (customerIdentity.isLoading || customerIdentity.isError))}
              statementIqPanel={statementCaptureApplies(methods.watch('individual.idType')) ? <StatementIqPanel
                applicantId={methods.watch('individual.idNumber')} draftId={statementDraftId}
                onReset={()=>{setStatementDraftId(undefined);sessionStorage.removeItem(STATEMENT_DRAFT_KEY);}}
                onDraft={id=>{setStatementDraftId(id);sessionStorage.setItem(STATEMENT_DRAFT_KEY,id);}} /> : undefined}
              creditCheckPanel={(
                <IndividualCreditCheckPanel
                  existingCustomer={isExistingCustomer === true}
                  assessment={individualCreditAssessment}
                  onAssessmentChange={handleIndividualCreditAssessment}
                />
              )}
            />
            </>
          );
        } else {
          return <CompanyInfoStep />;
        }
      case 3:
        return customerType === 'individual' ? <>
          <IndividualInfoStep section="income" />
        </> : <FundingRequestStep />;
      case 8:
        return customerType === 'individual' ? <IndividualQualificationStep onReady={setQualificationReady}
          bureauRevision={individualCreditAssessment?.result.enquiryResultId || individualCreditAssessment?.result.enquiryId
            || JSON.stringify(individualCreditAssessment?.result.report || null)} /> : null;
      case 9:
        return customerType === 'individual' ? <IndividualFundingStep showAffordability={false} /> : null;
      case 4:
        return customerType === 'individual' ? <IndividualInfoStep section="contact" /> : <ProductFundingDetailsStep />;
      case 5:
        return <BankAccountStep />;
      case 6:
        return customerType === 'individual' ? <IndividualInfoStep section="employment" /> : <CollateralStep existing={customerExposure} />;
      case 7:
        return <><ReviewStep individualCreditAssessment={individualCreditAssessment}
          statementIqPanel={customerType === 'individual' && statementCaptureApplies(methods.watch('individual.idType')) && statementDraftId
            ? <StatementIqPanel draftId={statementDraftId} applicantId={methods.watch('individual.idNumber')} readOnly /> : undefined}
          creditCheckPanel={customerType === 'individual' ? <IndividualCreditCheckPanel
            existingCustomer={isExistingCustomer === true}
            assessment={individualCreditAssessment}
            onAssessmentChange={handleIndividualCreditAssessment}
          /> : undefined} />
          </>;
      default:
        return 'Unknown step';
    }
  };

  // Display submission success state
  if (draftResumeError) return <div className="alert alert-danger m-4" role="alert">
    <p>{draftResumeError}</p>
    <button type="button" className="btn btn-outline-primary" onClick={() => window.location.reload()}>Retry loading draft</button>
  </div>;

  if (isSubmitSuccess) {
    // Get the last customer type value from the form
    const customerType = methods.getValues('customerType');

    return (
        <div className="container-fluid p-4">
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
              </div>
              <h2 className="mb-3">Application Submitted Successfully!</h2>
              <p className="text-muted mb-2">Your application number is:</p>
              <div className="application-number mb-4">{applicationNumber}</div>
              <p className="text-muted mb-4">We will review your application and get back to you shortly.</p>

              {/* Document Upload Information Box */}
              <div className="card mb-4 shadow-sm border-0 overflow-hidden">
                <div className="card-header bg-gradient text-white py-3" style={{ background: 'linear-gradient(45deg, #4e73df, #6f42c1)' }}>
                  <h5 className="mb-0 d-flex align-items-center">
                    <i className="bi bi-file-earmark-arrow-up me-2"></i>
                    Next Step: Document Upload
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className="row g-0">
                    <div className="col-lg-12 p-4">
                      <h5 className="fw-bold mb-3">Document Verification Required</h5>
                      <p className="text-muted mb-4">
                        Please upload the following documents to complete your application process:
                      </p>
                      <div className="row g-3 mb-4">
                        <div className="col-md-4">
                          <div className="d-flex p-3 rounded-3 h-100" style={{ background: 'rgba(78, 115, 223, 0.05)', border: '1px solid rgba(78, 115, 223, 0.1)' }}>
                            <div className="me-3 mt-1">
                            <span className="badge rounded-circle p-2" style={{ background: 'rgba(78, 115, 223, 0.2)' }}>
                              <i className="bi bi-bank text-primary"></i>
                            </span>
                            </div>
                            <div>
                              <h6 className="fw-bold mb-1">Bank Statements</h6>
                              <p className="small text-muted mb-0">Latest 3 months statements</p>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="d-flex p-3 rounded-3 h-100" style={{ background: 'rgba(78, 115, 223, 0.05)', border: '1px solid rgba(78, 115, 223, 0.1)' }}>
                            <div className="me-3 mt-1">
                            <span className="badge rounded-circle p-2" style={{ background: 'rgba(78, 115, 223, 0.2)' }}>
                              <i className="bi bi-house-door text-primary"></i>
                            </span>
                            </div>
                            <div>
                              <h6 className="fw-bold mb-1">Proof of Residence</h6>
                              <p className="small text-muted mb-0">Utility bill or lease agreement</p>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="d-flex p-3 rounded-3 h-100" style={{ background: 'rgba(78, 115, 223, 0.05)', border: '1px solid rgba(78, 115, 223, 0.1)' }}>
                            <div className="me-3 mt-1">
                            <span className="badge rounded-circle p-2" style={{ background: 'rgba(78, 115, 223, 0.2)' }}>
                              <i className="bi bi-envelope-paper text-primary"></i>
                            </span>
                            </div>
                            <div>
                              <h6 className="fw-bold mb-1">Bank Letter</h6>
                              <p className="small text-muted mb-0">Confirmation of bank details</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-center gap-3">
                <button
                    className="btn btn-primary rounded-pill px-4 py-2 d-flex align-items-center shadow-sm"
                    onClick={() => navigate(`/loan-application-documents/${submittedApplicationId}`)}
                >
                  <i className="bi bi-file-earmark-arrow-up me-2"></i>Upload Documents
                </button>
                <button
                    className="btn btn-outline-secondary rounded-pill px-4 py-2"
                    onClick={() => navigate('/applications')}
                >
                  View All Applications
                </button>
              </div>
            </div>
          </div>
        </div>
    );
  }

  return (
      <div ref={presentationRoot} className={`loan-application-wizard ${presentationLoanType === 'PERSONAL' ? 'individual-journey' : ''} ${clientFullScreen ? 'fullscreen' : ''}`}>
        {presentationLoanType === 'PERSONAL' && <ToastContainer position="top-right" autoClose={4000} hideProgressBar />}
        <div className="container-fluid h-100">
          <div className="row h-100">
            {/* Main content */}
            <div className="col-12 col-lg-9 main-content">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0" data-step-heading tabIndex={-1}>
                    {getStepTitle(activeStep)}
                    <span className="text-muted ms-2">
                    {activeStep >= 0 ? (methods.watch('loanType') === 'PERSONAL'
                      ? `Step ${individualJourneySteps.findIndex(step => step.index === activeStep) + 1} of ${individualJourneySteps.length}`
                      : `Step ${activeStep + 1} of ${steps.length}`) : ''}
                  </span>
                  {isResumingDraft ? (
                    <span className="badge bg-light text-muted fw-normal ms-2">
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Loading saved draft...
                    </span>
                  ) : draftSaveStatus === 'saving' ? (
                    <span className="badge bg-light text-muted fw-normal ms-2">Saving draft...</span>
                  ) : draftSaveStatus === 'saved' && draftSavedAt ? (
                    <span className="badge bg-light text-success fw-normal ms-2" title="Saved automatically; find it under Pending Submissions">
                      <i className="fa fa-check me-1"></i>
                      Draft saved {draftSavedAt.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  ) : draftSaveStatus === 'error' ? (
                    <span className="badge bg-warning text-dark fw-normal ms-2" title="The draft will be saved again with your next change">
                      <i className="fa fa-exclamation-triangle me-1"></i>
                      Draft not saved
                    </span>
                  ) : null}
                  </h5>
                  <div>
                    {methods.watch('loanType') === 'PERSONAL' && !location.pathname.startsWith('/loan-application/edit') && <button
                      type="button" className="btn btn-sm btn-outline-primary me-2"
                      disabled={isResumingDraft || draftSaveStatus === 'saving'} onClick={() => { void saveDraft(); }}>
                      {draftSaveStatus === 'saving' ? 'Saving draft…' : 'Save draft'}
                    </button>}
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={handleBack}
                        disabled={activeStep === 0}
                        title="Go to previous step"
                    >
                      <i className="bi bi-arrow-left"></i>
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={handleFullScreen}
                    >
                      <i className={`bi ${clientFullScreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <WizardCustomerContext.Provider value={customerData?.id || navState.customerId || undefined}>
                  <FormProvider {...methods}>
                    <form onSubmit={(e) => {
                      // If submission is not allowed, prevent it
                      if (justArrivedAtReviewStep || !allowSubmission) {
                        e.preventDefault();
                        console.log('Form submission prevented: just arrived or not allowed');
                        return;
                      }

                      // If submission is allowed, proceed
                      console.log('Form submission allowed, proceeding with submit');
                      e.preventDefault(); // Prevent default to handle manually
                      const data = methods.getValues();
                      handleSubmit(data);
                    }}>
                      {stepValidationMessages.length > 0 && methods.watch('loanType') === 'PERSONAL' && <IndividualErrorAlert
                        message={stepValidationMessages.find(message => message.includes('quotation')) || stepValidationMessages[0]}
                        notify="always" attempt={validationAttempt}>
                        <strong>Complete or correct these fields to continue:</strong>
                        <ul className="mb-0">{stepValidationMessages.map(message => <li key={message}>{message}</li>)}</ul>
                      </IndividualErrorAlert>}
                      {error && (
                        <IndividualErrorAlert message={`Application could not be saved. ${error}`} notify={methods.watch('loanType') === 'PERSONAL' ? 'always' : 'never'}>
                          <strong>Application could not be saved.</strong> {error}
                          {methods.getValues('customerType') === 'individual' && /maintenance/i.test(error) && <div className="mt-2">
                            <span>Complete Monthly Maintenance Payments in Income and Expenses → Monthly expenses. Enter 0 only if none applies.</span>
                            <button type="button" className="btn btn-outline-primary btn-sm ms-2" onClick={() => {
                              setActiveStep(3);
                              window.setTimeout(() => methods.setFocus('financial.maintenance'), 0);
                            }}>Edit Monthly Maintenance Payments</button>
                          </div>}
                        </IndividualErrorAlert>
                      )}
                      {getStepContent(activeStep)}
                      {activeStep >= 0 && (
                          <div className="d-flex justify-content-between mt-4 journey-actions">
                            <div className="d-flex gap-2">
                              <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => setShowAbandonModal(true)}
                              >
                                Cancel
                              </button>
                              <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  disabled={activeStep === 0}
                                  onClick={handleBack}
                              >
                                Back
                              </button>
                            </div>
                            <div className="d-flex gap-2">
                              {activeStep === 7 ? (
                                  <>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setIsCommentModalOpen(true)}
                                        title="Add a comment"
                                    >
                                      <i className="fa fa-comment me-1"></i>
                                      Comment
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={isLoading || justArrivedAtReviewStep || !allowSubmission}
                                        onClick={() => {
                                          if (!justArrivedAtReviewStep && allowSubmission) {
                                            console.log('Submit button clicked manually');
                                            const data = methods.getValues();
                                            handleSubmit(data);
                                          }
                                        }}
                                    >
                                      {isLoading ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Submitting...
                                          </>
                                      ) : justArrivedAtReviewStep ? (
                                          'Please Wait...'
                                      ) : (
                                          <>Submit Application & Continue to Documents</>
                                      )}
                                    </button>
                                  </>
                              ) : (
                                  <>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setIsCommentModalOpen(true)}
                                        title="Add a comment"
                                    >
                                      <i className="fa fa-comment me-1"></i>
                                      Comment
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleNext}
                                        disabled={(methods.watch('loanType') === 'PERSONAL' ? activeStep === 9 : activeStep === 3) && isLoanDateRangeInvalid}
                                    >
                                      Next
                                    </button>
                                  </>
                              )}
                            </div>
                          </div>
                      )}
                    </form>
                  </FormProvider>
                  </WizardCustomerContext.Provider>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-3 d-none d-lg-block">
              <div className="sidebar always-visible">
                <div className="sidebar-content">
                  <div className="sidebar-header">
                    <h4>Application Progress</h4>
                    <p className="text-muted">Complete all steps to submit your application</p>
                  </div>
                  <div className="steps-container">
                    {(methods.watch('loanType') === 'PERSONAL' ? individualJourneySteps : steps)
                      // An unsecured loan never visits Collateral, so it should not sit in the rail
                      // collecting a tick for work that was never done.
                      .filter((step) => !(methods.watch('loanType') !== 'PERSONAL' && isUnsecuredLoan && step.index === 6))
                      .map((step, position) => {
                        const completed = methods.watch('loanType') === 'PERSONAL'
                          ? individualJourneySteps.findIndex(item => item.index === activeStep) > position : activeStep > step.index;
                        return (
                        <div
                            key={step.index}
                            className={`step-item ${activeStep === step.index ? 'active' : ''} ${
                                completed ? 'completed' : ''
                            }`}
                        >
                          <div className="step-number">
                            {completed ? (
                                <i className="bi bi-check"></i>
                            ) : (
                                position + 1
                            )}
                          </div>
                          <div className="step-label">{step.label}</div>
                        </div>
                    ); })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comment Modal */}
        <CommentModal
          isOpen={isCommentModalOpen}
          onClose={() => setIsCommentModalOpen(false)}
          loanApplicationId={paramApplicationId || undefined}
          onCommentAdded={(comment) => {
            if (paramApplicationId) {
              // For existing applications, comments are added via API
            } else {
              // For new applications, collect comments
              setCollectedComments(prev => [...prev, comment]);
              toast.success('Comment added successfully');
            }
          }}
        />

        {/* Abandon Application Modal */}
        {showAbandonModal && (
          <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.3)' }}>
                      <i className="fa fa-times-circle text-danger" style={{ display: 'block' }}></i>
                    </div>
                    <div>
                      <h5 className="modal-title mb-0 fw-bold">Abandon Application</h5>
                      <p className="text-muted mb-0 small">This action cannot be undone</p>
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setShowAbandonModal(false)}></button>
                </div>
                <div className="modal-body p-4">
                  <p className="text-muted mb-0">
                    Are you sure you want to abandon this application? Everything captured so far, including its saved draft, will be deleted and you will be redirected to the applications page.
                  </p>
                </div>
                <div className="modal-footer border-0 px-4 pb-4">
                  <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowAbandonModal(false)}>
                    Continue Editing
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger rounded-pill px-4"
                    onClick={() => {
                      setShowAbandonModal(false);
                      void removeDraft();
                      toast.success('Application abandoned successfully');
                      setTimeout(() => navigate('/applications'), 1500);
                    }}
                  >
                    <i className="fa fa-times-circle me-2"></i>
                    Yes, Abandon
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
